import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Line, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import TeamsSettings from './utilization/TeamsSettings';
import { getConfig } from '../runtimeConfig';
import { useTheme, ThemeToggle } from '../ThemeContext';

// Lazy load 3D component for performance
const Warehouse3D = lazy(() => import('./Warehouse3D'));

const _cfg = getConfig();
const API_BASE = _cfg.REACT_APP_API;
const AI_API_BASE = _cfg.REACT_APP_AI_API;

// CACHE SYSTEM
const CACHE_KEY = 'utilization_dashboard_cache';

const getCache = () => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch { return null; }
};

const setCache = (data: any) => {
  try {
    const existing = getCache() || {};
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...existing, ...data, timestamp: Date.now() }));
  } catch {}
};

const clearCache = () => sessionStorage.removeItem(CACHE_KEY);

// Warehouse display names mapping
const WAREHOUSE_NAMES: Record<string, string> = {
  'DPW': 'Dammam port WH',
  'DSI': 'Dammam 2nd',
  'JDA': 'Jeddah Modon3A',
  'JDB': 'Jeddah 3B',
  'JUB': 'Jubail WH',
  'JUB_1': 'JUBAIL 1 WH',
  'JUB_2': 'JUBAIL 2',
  'RUH': 'Hala Riyadh WH',
  'RUH_M': 'HALA Riyadh WH'
};

const getWarehouseDisplayName = (code: string): string => WAREHOUSE_NAMES[code] || code;


const getLogicalWarehouseStatic = (physicalWarehouse: string, storageArea: string | null): string => {
  if (physicalWarehouse === 'JUB' && storageArea) {
    if (storageArea === 'CHM-01' || storageArea === 'CHM-02') return 'JUB_1';
    return 'JUB_2';
  }
  return physicalWarehouse;
};

// ============================================================
// TYPES
// ============================================================
interface User { id: number; username: string; display_name: string; role: 'admin' | 'warehouse_supervisor'; assigned_warehouse: string | null; }
interface OverviewData { CAPACITY: number; TOTAL_PALLETS: number; FREE_PALLETS: number; UTILIZATION_PCT: number; TEMP_BLOCKED_PALLETS?: number; TEMP_BLOCKED_BINS?: number; }
interface WarehouseData { WAREHOUSE: string; CAPACITY: number; PALLET_COUNT: number; FREE_COUNT: number; UTILIZATION_PCT: number; TEMP_BLOCKED?: number; NORMAL_PALLETS?: number; SOURCE?: 'IBM' | 'MANUAL' | 'MERGED'; [key: string]: string | number | undefined; }
interface AreaData { AREA: string; CAPACITY: number; PALLET_COUNT: number; FREE_COUNT: number; CLIENT_COUNT: number; UTILIZATION_PCT: number; [key: string]: string | number; }
interface ClientData { CLIENT: string; CURRENT_PALLETS: number; PREVIOUS_PALLETS: number; SOURCE?: 'IBM' | 'MANUAL' | 'MERGED' | 'SNAPSHOT'; [key: string]: string | number | undefined; }
interface ManualWeeklyEntry { id: number; week_number: number; year: number; warehouse: string; warehouse_area: string; area_capacity: number; client_name: string; opening_balance: number; inbound: number; outbound: number; closing_balance: number; capacity: number; uom: string; total_qty: number; notes: string; entered_by: string; created_at: string; updated_at: string; }
interface UOMData { UOM: string; PALLET_COUNT: number; TOTAL_QTY: number; RESERVED_QTY: number; ITEM_COUNT: number; AREA_COUNT: number; [key: string]: string | number; }
interface ClientSummary { TOTAL_PALLETS: number; UOM_COUNT: number; TOTAL_ITEMS: number; TOTAL_AREAS: number; WAREHOUSE_COUNT: number; }
interface ItemData { ITEM_CODE: string; ITEM_NAME: string; UOM: string; PALLET_COUNT: number; AVAILABLE_QTY: number; RESERVED_QTY: number; BATCH_COUNT: number; WAREHOUSE: string; AREA_COUNT: number; }
interface RawWarehouseItem { WAREHOUSE: string; STORAGE_AREA: string; TOTAL_NO_OF_SEGMENTS: number; FREE_SEGMENTS: number; NUMBER_OF_LEVELS?: number; LPFSTA?: string; OCCUPANCY_STATUS?: string; }

// Pallet capacity calculation: small bins (<=4 segments) use level-based rule
const SMALL_BIN_THRESHOLD = 10;
// Cap: seg 1-2 → as-is, seg 3-9 + level=10 → 2, seg 3-9 + level≠10 → 1, seg ≥10 → as-is.
const calcPalletCapacity = (totalSegments: number, level: number): number => {
  if (totalSegments <= 0) return 0;
  if (totalSegments <= 2) return totalSegments;
  if (totalSegments < SMALL_BIN_THRESHOLD) return level === 10 ? 2 : 1;
  return totalSegments;
};
const calcUsedPallets = (totalSegments: number, freeSegments: number, _level: number): number => {
  if (totalSegments === 0) return 0;
  return Math.max(0, totalSegments - freeSegments);
};
interface CustomerStorageItem { WAREHOUSE: string; CUSTOMER: string; STORAGE_LOCATION: string; STORAGE_TYPE: string; USED_PALLETS: number; TOTAL_CAPACITY: number; AVAILABLE_CAPACITY: number; }
interface WarehouseMapping { id: number; physical_warehouse: string; storage_area: string | null; logical_warehouse: string; display_name: string; is_active: boolean; }
interface BinStats { TOTAL_PALLET_CAPACITY: number; PHYSICAL_BINS: number; VIRTUAL_BINS: number; FREE_PALLETS: number; OCCUPIED_BINS: number; PARTIAL_BINS: number; FREE_PCT: number; USED_PCT: number; }
interface WarehouseBinData { WAREHOUSE: string; TOTAL_BINS: number; FREE_BINS: number; OCCUPIED_BINS: number; PARTIAL_BINS: number; UTILIZATION_PCT: number; }
interface BinOccupancyData { totalBins: number; binsLow: number; binsHigh: number; binsEmpty: number; lowPct: number; highPct: number; emptyPct: number; }
interface UserData { id: number; username: string; display_name: string; role: 'admin' | 'warehouse_supervisor'; assigned_warehouse: string | null; email: string | null; is_active: boolean; created_at: string; last_login: string | null; }

// ============================================================
// COMPACT POWER BI COLORS
// ============================================================
const COLORS = {
  primary: '#118DFF', secondary: '#1B2631', accent: '#E66C37', success: '#6B8E23', warning: '#FFC000', danger: '#C83D3D',
  gray: '#A6A6A6', lightGray: '#F2F2F2', darkGray: '#333333', background: '#F5F5F5', cardBg: '#FFFFFF',
  text: '#252423', textLight: '#605E5C', border: '#E8E8E8', manual: '#9B59B6', merged: '#27AE60',
};
const UOM_COLORS = [COLORS.primary, COLORS.success, COLORS.accent, COLORS.danger, '#7B68EE', '#20B2AA', '#FF69B4', '#4169E1'];

// ============================================================
// COMPACT STYLES - REDUCED SIZES (theme-aware via CSS variables)
// ============================================================
const styles: { [key: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', backgroundColor: 'var(--theme-background)', fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, sans-serif', display: 'flex' },
  
  // SIDEBAR - FIXED LAYOUT with modern theme-aware styling
  sidebar: {
    background: `linear-gradient(180deg, var(--theme-card) 0%, var(--theme-background-secondary) 100%)`,
    color: 'var(--theme-text)',
    padding: '0',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '4px 0 20px var(--theme-shadow)',
    position: 'sticky' as const,
    top: 0,
    height: '100vh',
    width: '210px',
    minWidth: '210px',
    zIndex: 50,
    overflow: 'hidden',
    borderRight: '1px solid var(--theme-border)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  sidebarLogo: { padding: '16px 14px', borderBottom: '1px solid var(--theme-border-light)', background: 'rgba(0,0,0,0.03)', flex: '0 0 auto' },
  sidebarNav: { padding: '12px 0', flex: '0 0 auto' },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', color: 'var(--theme-text-secondary)', cursor: 'pointer', fontSize: '13px', fontWeight: '500', borderLeft: '3px solid transparent', margin: '2px 0', transition: 'all 0.2s ease' },
  navItemActive: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', color: 'var(--theme-primary)', cursor: 'pointer', fontSize: '13px', fontWeight: '600', background: 'rgba(var(--theme-primary-rgb), 0.1)', borderLeft: '3px solid var(--theme-primary)', margin: '2px 0', transition: 'all 0.2s ease' },
  
  // WEEK NUMBERS - flex grow to fill space
  weekNumbers: { padding: '14px', borderTop: '1px solid var(--theme-border-light)', flex: '1 1 auto', overflow: 'auto', minHeight: '100px' },
  weekGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px' },
  weekBtn: { padding: '8px 4px', backgroundColor: 'var(--theme-background-secondary)', border: 'none', borderRadius: '5px', color: 'var(--theme-text-secondary)', fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  weekBtnActive: { padding: '8px 4px', background: 'linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-accent) 100%)', border: 'none', borderRadius: '5px', color: 'white', fontSize: '12px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 2px 6px rgba(17,141,255,0.4)' },
  
  // USER INFO - flex 0 0 auto at bottom, NO absolute positioning
  userInfo: { padding: '14px', background: 'rgba(0,0,0,0.15)', borderTop: '1px solid var(--theme-border-light)', flex: '0 0 auto' },
  
  mainContent: { padding: '18px 24px', flex: 1, minHeight: '100vh', overflow: 'auto', backgroundColor: 'var(--theme-background)' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '18px' },
  chartsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '18px' },
  chartCard: { backgroundColor: 'var(--theme-card)', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 12px var(--theme-shadow)', border: '1px solid var(--theme-border-light)' },
  chartTitle: { fontSize: '15px', fontWeight: '700', color: 'var(--theme-text)', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  fullWidthCard: { backgroundColor: 'var(--theme-card)', borderRadius: '12px', padding: '18px', boxShadow: '0 2px 12px var(--theme-shadow)', border: '1px solid var(--theme-border-light)', marginBottom: '18px' },
  legendRow: { display: 'flex', gap: '14px', marginBottom: '10px' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--theme-text-secondary)' },
  legendDot: { width: '9px', height: '9px', borderRadius: '2px' },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '12px' },
  th: { textAlign: 'left' as const, padding: '10px 12px', backgroundColor: 'var(--theme-background-secondary)', fontWeight: '600', color: 'var(--theme-text)', borderBottom: '1px solid var(--theme-border-light)', fontSize: '11px' },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--theme-border-light)', color: 'var(--theme-text)' },
  modalOverlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(2px)' },
  modal: { backgroundColor: 'var(--theme-card)', borderRadius: '12px', width: '500px', maxHeight: '88vh', overflow: 'auto', boxShadow: '0 4px 20px var(--theme-shadow-lg)', border: '1px solid var(--theme-border-light)' },
  largeModal: { backgroundColor: 'var(--theme-card)', borderRadius: '12px', width: '800px', maxWidth: '94vw', maxHeight: '88vh', overflow: 'auto', boxShadow: '0 4px 20px var(--theme-shadow-lg)', border: '1px solid var(--theme-border-light)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--theme-border-light)' },
  modalTitle: { fontSize: '16px', fontWeight: '600', color: 'var(--theme-text)', margin: 0 },
  modalContent: { padding: '18px' },
  closeBtn: { background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--theme-text-secondary)', padding: '0', transition: 'color 0.2s' },
  actionBtn: { padding: '5px 10px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', marginRight: '4px' },
  addBtn: { padding: '8px 14px', backgroundColor: 'var(--theme-primary)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '12px' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' },
  summaryCard: { backgroundColor: 'var(--theme-background-secondary)', borderRadius: '6px', padding: '12px', textAlign: 'center' as const },
  summaryValue: { fontSize: '20px', fontWeight: '600', color: 'var(--theme-primary)' },
  summaryLabel: { fontSize: '10px', color: 'var(--theme-text-secondary)', marginTop: '4px' },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 12px', backgroundColor: 'var(--theme-background-secondary)', color: 'var(--theme-text)', border: '1px solid var(--theme-border-light)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', marginBottom: '12px' },
  itemsTable: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '11px' },
  itemsTh: { textAlign: 'left' as const, padding: '8px 10px', backgroundColor: 'var(--theme-background-secondary)', fontWeight: '600', color: 'var(--theme-text)', borderBottom: '1px solid var(--theme-border-light)', fontSize: '10px' },
  itemsTd: { padding: '8px 10px', borderBottom: '1px solid var(--theme-border-light)', fontSize: '11px', color: 'var(--theme-text)' },
  comboboxContainer: { position: 'relative' as const },
  comboboxDropdown: { position: 'absolute' as const, top: '100%', left: 0, right: 0, backgroundColor: 'var(--theme-card)', border: '1px solid var(--theme-border-light)', borderRadius: '4px', boxShadow: '0 4px 12px var(--theme-shadow)', maxHeight: '180px', overflow: 'auto', zIndex: 100, marginTop: '2px' },
  comboboxOption: { padding: '8px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid var(--theme-border-light)', color: 'var(--theme-text)', transition: 'background-color 0.2s' },
  comboboxAddNew: { padding: '8px 10px', cursor: 'pointer', fontSize: '12px', backgroundColor: 'rgba(var(--theme-primary-rgb), 0.1)', color: 'var(--theme-primary)', fontWeight: '500' },
  newBadge: { display: 'inline-block', padding: '2px 6px', backgroundColor: 'var(--theme-warning)', color: 'white', borderRadius: '3px', fontSize: '9px', fontWeight: '600', marginLeft: '6px' },
  sourceBadge: { display: 'inline-block', padding: '2px 6px', borderRadius: '3px', fontSize: '9px', fontWeight: '600', marginLeft: '6px' },
  input: { width: '100%', padding: '10px 12px', border: '1.5px solid var(--theme-border)', borderRadius: '6px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const, backgroundColor: 'var(--theme-background-secondary)', color: 'var(--theme-text)', transition: 'border-color 0.2s' },
  inputGroup: { marginBottom: '16px' },
  inputLabel: { display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--theme-text)', marginBottom: '6px' },
  loginContainer: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, var(--theme-background) 0%, var(--theme-background-secondary) 100%)` },
  loginBox: { backgroundColor: 'var(--theme-card)', borderRadius: '12px', padding: '36px 32px', width: '380px', boxShadow: '0 20px 40px rgba(0,0,0,0.25)' },
  loginTitle: { fontSize: '24px', fontWeight: '600', color: 'var(--theme-primary)', margin: '10px 0 4px', textAlign: 'center' as const },
  loginSubtitle: { fontSize: '13px', color: 'var(--theme-text-secondary)', textAlign: 'center' as const },
  loginBtn: { width: '100%', padding: '12px', backgroundColor: 'var(--theme-primary)', color: 'white', border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  error: { color: 'var(--theme-danger)', fontSize: '12px', marginBottom: '12px', textAlign: 'center' as const },
};

declare global { interface Date { getWeek(): number; } }
Date.prototype.getWeek = function(): number {
  const d = new Date(Date.UTC(this.getFullYear(), this.getMonth(), this.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

// ============================================================
// SNAPSHOT INDICATOR
// ============================================================
const SnapshotIndicator: React.FC<{ dataSource: 'SNAPSHOT' | 'REALTIME'; snapshotDate: string | null; week: number; year: number; }> = ({ dataSource, snapshotDate, week }) => {
  if (dataSource === 'SNAPSHOT') {
    const d = snapshotDate ? new Date(snapshotDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    return <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '14px', fontSize: '11px', fontWeight: 500 }}><span>📸</span>Snapshot{d && ` (${d})`}</div>;
  }
  return <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', backgroundColor: '#fef3c7', color: '#92400e', borderRadius: '14px', fontSize: '11px', fontWeight: 500 }}><span>⚡</span>Real-time (No W{week})</div>;
};

// ============================================================
// CAPTURE SNAPSHOT BUTTON
// ============================================================
const CaptureSnapshotButton: React.FC<{ week: number; year: number; token: string; userRole: string; dataSource: 'SNAPSHOT' | 'REALTIME'; onCapture: () => void; }> = ({ week, year, token, userRole, dataSource, onCapture }) => {
  const [capturing, setCapturing] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  if (userRole !== 'admin' || dataSource === 'SNAPSHOT') return null;
  const handleCapture = async () => {
    if (!window.confirm(`Capture snapshot for Week ${week}, ${year}?`)) return;
    setCapturing(true); setResult(null);
    try {
      const res = await fetch(`${API_BASE}/snapshots/capture`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ week, year }) });
      const data = await res.json();
      if (data.success) { setResult({ success: true, message: `✅ ${data.data.clientsCaptured} clients` }); setTimeout(() => { setResult(null); onCapture(); }, 1500); }
      else setResult({ success: false, message: data.error || 'Failed' });
    } catch (e: any) { setResult({ success: false, message: e.message }); }
    setCapturing(false);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <button onClick={handleCapture} disabled={capturing} style={{ padding: '6px 12px', backgroundColor: capturing ? '#9ca3af' : '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: capturing ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>{capturing ? '⏳...' : <>📸 Capture W{week}</>}</button>
      {result && <span style={{ fontSize: '11px', color: result.success ? '#166534' : '#dc2626', fontWeight: 500 }}>{result.message}</span>}
    </div>
  );
};

// ============================================================
// SOURCE BADGE
// ============================================================
const SourceBadge: React.FC<{ source?: 'IBM' | 'MANUAL' | 'MERGED' }> = ({ source }) => {
  if (!source || source === 'IBM') return null;
  const c = { MANUAL: { bg: '#F3E5F5', color: COLORS.manual }, MERGED: { bg: '#E8F5E9', color: COLORS.merged } };
  const { bg, color } = c[source] || { bg: '#E3F2FD', color: COLORS.primary };
  return <span style={{ ...styles.sourceBadge, backgroundColor: bg, color }}>{source}</span>;
};

// ============================================================
// COMBOBOX
// ============================================================
const ComboBox: React.FC<{ value: string; onChange: (v: string) => void; options: string[]; placeholder: string; disabled?: boolean; displayFn?: (v: string) => string; }> = ({ value, onChange, options, placeholder, disabled, displayFn }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const display = displayFn || ((v: string) => v);
  const filtered = useMemo(() => searchTerm ? options.filter(o => o.toLowerCase().includes(searchTerm.toLowerCase()) || display(o).toLowerCase().includes(searchTerm.toLowerCase())) : options, [options, searchTerm, display]);
  const isNew = value && !options.includes(value);
  const showAddNew = searchTerm && !options.some(o => o.toLowerCase() === searchTerm.toLowerCase());
  return (
    <div style={styles.comboboxContainer}>
      <div style={{ position: 'relative' }}>
        <input type="text" value={displayFn ? display(value) : value} onChange={(e) => { setSearchTerm(e.target.value); onChange(e.target.value); setIsOpen(true); }} onFocus={() => setIsOpen(true)} onBlur={() => setTimeout(() => setIsOpen(false), 200)} placeholder={placeholder} disabled={disabled} style={{ ...styles.input, borderColor: isNew ? COLORS.warning : COLORS.border, backgroundColor: disabled ? COLORS.lightGray : 'white' }} />
        {isNew && <span style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', ...styles.newBadge }}>NEW</span>}
      </div>
      {isOpen && !disabled && (
        <div style={styles.comboboxDropdown}>
          {filtered.length > 0 ? filtered.map((o, i) => <div key={o} style={{ ...styles.comboboxOption, backgroundColor: 'white' }} onClick={() => { onChange(o); setSearchTerm(''); setIsOpen(false); }}>{display(o)}</div>) : <div style={{ ...styles.comboboxOption, color: COLORS.gray }}>No matches</div>}
          {showAddNew && <div style={styles.comboboxAddNew} onClick={() => { onChange(searchTerm); setIsOpen(false); }}>+ Add "{searchTerm}"</div>}
        </div>
      )}
    </div>
  );
};

// ============================================================
// LOGIN
// ============================================================
const LoginPage: React.FC<{ onLogin: (u: User, t: string) => void }> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password }) });
      const d = await r.json();
      if (d.success) onLogin(d.user, d.token);
      else setError(d.error || 'Invalid credentials');
    } catch { setError('Unable to connect to server'); }
    setLoading(false);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '14px 14px 14px 42px', fontSize: '14px',
    border: '1.5px solid #e2e8f0', borderRadius: '12px', outline: 'none',
    backgroundColor: '#f8fafc', color: '#0f172a', fontWeight: 500,
    transition: 'all 0.2s ease', boxSizing: 'border-box' as const,
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  };
  const focusInput = (e: React.FocusEvent<HTMLInputElement>) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.1)'; };
  const blurInput = (e: React.FocusEvent<HTMLInputElement>) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.boxShadow = 'none'; };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 35%, #312e81 60%, #0f172a 100%)',
      position: 'relative' as const, overflow: 'hidden',
      fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    }}>
      {/* Animated background orbs */}
      <div style={{ position: 'absolute', top: '-20%', left: '-10%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)', borderRadius: '50%', animation: 'loginPulse 8s ease-in-out infinite' }} />
      <div style={{ position: 'absolute', bottom: '-15%', right: '-5%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)', borderRadius: '50%', animation: 'loginPulse 6s ease-in-out infinite reverse' }} />
      <div style={{ position: 'absolute', top: '40%', right: '20%', width: '200px', height: '200px', background: 'radial-gradient(circle, rgba(6,182,212,0.08) 0%, transparent 70%)', borderRadius: '50%' }} />
      {/* Grid pattern */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: '460px', padding: '0 24px' }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.97)', borderRadius: '28px',
          padding: '48px 44px', boxShadow: '0 32px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.1)',
          backdropFilter: 'blur(24px)',
        }}>
          {/* Logo + Branding */}
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <div style={{
              width: '80px', height: '80px', borderRadius: '20px', margin: '0 auto 20px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
              overflow: 'hidden', background: '#fff',
            }}>
              <img src={`${process.env.PUBLIC_URL || ''}/halapic.jpg`} alt="HALA" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { const el = e.currentTarget as HTMLImageElement; el.style.display = 'none'; if (el.parentElement) { el.parentElement.style.background = 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'; el.parentElement.style.display = 'flex'; el.parentElement.style.alignItems = 'center'; el.parentElement.style.justifyContent = 'center'; el.parentElement.innerHTML = '<span style="font-size:36px;font-weight:800;color:white">H</span>'; }}} />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.03em' }}>HALA Supply Chain</h1>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 4px', fontWeight: 500, letterSpacing: '0.01em' }}>Warehouse Utilization Management</p>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '12px', padding: '6px 14px',
              background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.06) 100%)',
              borderRadius: '20px', border: '1px solid rgba(99,102,241,0.12)',
            }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10b981', boxShadow: '0 0 8px rgba(16,185,129,0.4)' }} />
              <span style={{ fontSize: '11px', color: '#6366f1', fontWeight: 600, letterSpacing: '0.02em' }}>SECURE ACCESS</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{ padding: '12px 16px', marginBottom: '20px', borderRadius: '12px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '16px' }}>⚠️</span>
                <span style={{ fontSize: '13px', color: '#dc2626', fontWeight: 500 }}>{error}</span>
              </div>
            )}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '8px', letterSpacing: '0.02em' }}>Username</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#94a3b8' }}>👤</span>
                <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Enter your username" required style={inputStyle} onFocus={focusInput} onBlur={blurInput} />
              </div>
            </div>
            <div style={{ marginBottom: '28px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '8px', letterSpacing: '0.02em' }}>Password</label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#94a3b8' }}>🔒</span>
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required style={{ ...inputStyle, paddingRight: '44px' }} onFocus={focusInput} onBlur={blurInput} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#94a3b8', padding: '4px' }}>{showPassword ? '👁️' : '👁️‍🗨️'}</button>
              </div>
            </div>
            <button type="submit" disabled={loading} style={{
              width: '100%', padding: '15px',
              background: loading ? '#94a3b8' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              color: 'white', border: 'none', borderRadius: '14px', fontSize: '15px', fontWeight: 700,
              cursor: loading ? 'wait' : 'pointer',
              boxShadow: loading ? 'none' : '0 8px 24px rgba(99,102,241,0.35)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', letterSpacing: '0.02em',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}
              onMouseEnter={(e) => { if (!loading) { (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 32px rgba(99,102,241,0.45)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}}
              onMouseLeave={(e) => { if (!loading) { (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(99,102,241,0.35)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}}
            >
              {loading ? (<><span style={{ display: 'inline-block', width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'loginSpin 0.6s linear infinite' }} /> Signing in...</>) : 'Sign In'}
            </button>
          </form>

          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #f1f5f9' }}>
            <p style={{ fontSize: '11px', color: '#cbd5e1', margin: 0, letterSpacing: '0.02em' }}>HALA Warehouse Intelligence Platform</p>
          </div>
        </div>
        <p style={{ textAlign: 'center', fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginTop: '24px', letterSpacing: '0.02em' }}>Powered by HALA Supply Chain Solutions</p>
      </div>
      <style>{`@keyframes loginPulse { 0%, 100% { transform: scale(1); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 1; } } @keyframes loginSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ============================================================
// BIN OCCUPANCY SECTION
// ============================================================
const BinOccupancySection: React.FC<{ token: string | null; selectedWarehouse: string | null; warehouses: WarehouseData[]; }> = ({ selectedWarehouse, warehouses }) => {
  const [loading, setLoading] = useState(true);
  const [binStats, setBinStats] = useState<BinStats | null>(null);
  const [warehouseBins, setWarehouseBins] = useState<WarehouseBinData[]>([]);
  const [binOccupancy, setBinOccupancy] = useState<BinOccupancyData | null>(null);
  const [selectedWH, setSelectedWH] = useState<string | null>(selectedWarehouse);

  const fetchBinData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, whRes, occRes] = await Promise.all([
        fetch(selectedWH ? `${API_BASE}/utilization/bin-stats?warehouse=${selectedWH}` : `${API_BASE}/utilization/bin-stats`).catch(() => null),
        fetch(`${API_BASE}/utilization/bins-by-warehouse`).catch(() => null),
        fetch(selectedWH ? `${API_BASE}/utilization/bin-occupancy?warehouse=${selectedWH}` : `${API_BASE}/utilization/bin-occupancy`).catch(() => null),
      ]);
      if (statsRes?.ok) { const d = await statsRes.json(); if (d.success) setBinStats(d.data); }
      if (whRes?.ok) { const d = await whRes.json(); if (d.success) setWarehouseBins(d.data || []); }
      if (occRes?.ok) { const d = await occRes.json(); if (d.success) setBinOccupancy(d.data); }
    } catch (e) { console.error('Bin fetch error:', e); }
    setLoading(false);
  }, [selectedWH]);

  useEffect(() => { fetchBinData(); }, [fetchBinData]);

  const fmt = (n: number | undefined) => n?.toLocaleString() || '0';

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', color: COLORS.textLight }}><div style={{ textAlign: 'center' }}><div style={{ fontSize: '36px', marginBottom: '12px' }}>📦</div><div style={{ fontSize: '13px' }}>Loading Bin Analysis...</div></div></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div><h2 style={{ fontSize: '22px', fontWeight: '700', color: COLORS.text, margin: 0 }}>Bin Occupancy Analysis</h2><p style={{ fontSize: '12px', color: COLORS.textLight, margin: '2px 0 0' }}>Physical bin locations</p></div>
        <select value={selectedWH || ''} onChange={(e) => setSelectedWH(e.target.value || null)} style={{ padding: '6px 10px', borderRadius: '6px', border: `1px solid ${COLORS.border}`, fontSize: '12px', minWidth: '140px' }}><option value="">All Warehouses</option>{warehouses.map(w => <option key={w.WAREHOUSE} value={w.WAREHOUSE}>{getWarehouseDisplayName(w.WAREHOUSE)}</option>)}</select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
        <div style={{ background: 'linear-gradient(135deg, #1B3A57 0%, #0D2137 100%)', borderRadius: '10px', padding: '16px', color: 'white' }}><div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '4px' }}>{fmt(binStats?.PHYSICAL_BINS)}</div><div style={{ fontSize: '10px', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Physical Bins</div></div>
        <div style={{ background: 'linear-gradient(135deg, #27AE60 0%, #1E8449 100%)', borderRadius: '10px', padding: '16px', color: 'white' }}><div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '4px' }}>{fmt(binStats?.FREE_PALLETS)}</div><div style={{ fontSize: '10px', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Free ({binStats?.FREE_PCT?.toFixed(1) || 0}%)</div></div>
        <div style={{ background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)', borderRadius: '10px', padding: '16px', color: 'white' }}><div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '4px' }}>{fmt(binStats?.OCCUPIED_BINS)}</div><div style={{ fontSize: '10px', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Occupied</div></div>
        <div style={{ background: 'linear-gradient(135deg, #F39C12 0%, #D68910 100%)', borderRadius: '10px', padding: '16px', color: 'white' }}><div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '4px' }}>{fmt(binStats?.PARTIAL_BINS)}</div><div style={{ fontSize: '10px', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Partial</div></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
        <div style={styles.chartCard}><h3 style={styles.chartTitle}>Bin Status Distribution</h3><div style={{ height: '220px' }}><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={[{ name: 'Free', value: binStats?.FREE_PALLETS || 0 }, { name: 'Occupied', value: binStats?.OCCUPIED_BINS || 0 }, { name: 'Partial', value: binStats?.PARTIAL_BINS || 0 }].filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name}: ${value}`}><Cell fill="#27AE60" /><Cell fill="#E74C3C" /><Cell fill="#F39C12" /></Pie><Tooltip formatter={(v: number) => fmt(v) + ' bins'} /></PieChart></ResponsiveContainer></div></div>
        <div style={styles.chartCard}><h3 style={styles.chartTitle}>Bin Segment Usage</h3><div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '8px 0' }}>
          <div><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span style={{ fontSize: '12px', fontWeight: 600 }}>Low (≤2 seg)</span><span style={{ fontSize: '13px', fontWeight: 700, color: '#E67E22' }}>{binOccupancy?.lowPct?.toFixed(1) || '0'}%</span></div><div style={{ width: '100%', height: '8px', backgroundColor: '#E8E8E8', borderRadius: '4px', overflow: 'hidden' }}><div style={{ width: `${binOccupancy?.lowPct || 0}%`, height: '100%', background: '#F39C12', borderRadius: '4px' }} /></div></div>
          <div><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span style={{ fontSize: '12px', fontWeight: 600 }}>High (&gt;2 seg)</span><span style={{ fontSize: '13px', fontWeight: 700, color: '#27AE60' }}>{binOccupancy?.highPct?.toFixed(1) || '0'}%</span></div><div style={{ width: '100%', height: '8px', backgroundColor: '#E8E8E8', borderRadius: '4px', overflow: 'hidden' }}><div style={{ width: `${binOccupancy?.highPct || 0}%`, height: '100%', background: '#27AE60', borderRadius: '4px' }} /></div></div>
          <div><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}><span style={{ fontSize: '12px', fontWeight: 600 }}>Empty</span><span style={{ fontSize: '13px', fontWeight: 700, color: '#95A5A6' }}>{binOccupancy?.emptyPct?.toFixed(1) || '0'}%</span></div><div style={{ width: '100%', height: '8px', backgroundColor: '#E8E8E8', borderRadius: '4px', overflow: 'hidden' }}><div style={{ width: `${binOccupancy?.emptyPct || 0}%`, height: '100%', background: '#95A5A6', borderRadius: '4px' }} /></div></div>
        </div></div>
      </div>
      <div style={styles.chartCard}><h3 style={styles.chartTitle}>Bin Status by Warehouse</h3><div style={{ height: '280px' }}><ResponsiveContainer width="100%" height="100%"><BarChart data={warehouseBins} margin={{ top: 10, right: 20, left: 10, bottom: 50 }} barCategoryGap="20%"><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8E8" /><XAxis dataKey="WAREHOUSE" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={50} tickFormatter={getWarehouseDisplayName} /><YAxis tick={{ fontSize: 10 }} /><Tooltip formatter={(v: number) => fmt(v) + ' bins'} /><Bar dataKey="FREE_BINS" fill="#27AE60" name="Free" stackId="a" /><Bar dataKey="PARTIAL_BINS" fill="#F39C12" name="Partial" stackId="a" /><Bar dataKey="OCCUPIED_BINS" fill="#E74C3C" name="Occupied" stackId="a" radius={[3, 3, 0, 0]} /></BarChart></ResponsiveContainer></div></div>
    </div>
  );
};

// ============================================================
// WEEKLY ENTRY MODAL
// ============================================================
const WeeklyEntryModal: React.FC<{ isOpen: boolean; onClose: () => void; onSave: () => void; user: User | null; token: string | null; editEntry: ManualWeeklyEntry | null; warehouses: string[]; clients: string[]; allEntries: ManualWeeklyEntry[]; }> = ({ isOpen, onClose, onSave, user, token, editEntry, warehouses, clients, allEntries }) => {
  const UOM_OPTIONS = ['PLT', 'CTN', 'PCS', 'KG', 'BOX', 'EA', 'CS', 'PAL', 'BAG', 'ROLL'];
  const [formData, setFormData] = useState({ week_number: new Date().getWeek(), year: new Date().getFullYear(), warehouse: user?.assigned_warehouse || '', warehouse_area: '', area_capacity: 0, client_name: '', opening_balance: 0, inbound: 0, outbound: 0, closing_balance: 0, capacity: 0, uom: 'PLT', total_qty: 0, notes: '' });
  const [saving, setSaving] = useState(false);
  const [customUom, setCustomUom] = useState('');

  // Get previous entry data for same warehouse/client (opening balance, capacity, area)
  const getPreviousEntryData = useCallback((wh: string, client: string, week: number, year: number) => {
    if (!wh || !client) return { closing: 0, capacity: 0, area_capacity: 0, warehouse_area: '' };
    const relevantEntries = allEntries.filter(e =>
      e.warehouse === wh && e.client_name === client &&
      (e.year < year || (e.year === year && e.week_number < week))
    );
    if (relevantEntries.length === 0) return { closing: 0, capacity: 0, area_capacity: 0, warehouse_area: '' };
    relevantEntries.sort((a, b) => b.year - a.year || b.week_number - a.week_number);
    const prev = relevantEntries[0];
    return { closing: prev?.closing_balance || 0, capacity: prev?.capacity || 0, area_capacity: prev?.area_capacity || 0, warehouse_area: prev?.warehouse_area || '' };
  }, [allEntries]);

  useEffect(() => { setFormData(p => ({ ...p, closing_balance: Math.max(0, p.opening_balance + p.inbound - p.outbound) })); }, [formData.opening_balance, formData.inbound, formData.outbound]);

  // Update opening balance when warehouse/client/week changes (for new entries only)
  useEffect(() => {
    if (!editEntry && formData.warehouse && formData.client_name) {
      const prev = getPreviousEntryData(formData.warehouse, formData.client_name, formData.week_number, formData.year);
      setFormData(p => ({
        ...p,
        opening_balance: prev.closing,
        closing_balance: Math.max(0, prev.closing + p.inbound - p.outbound),
        capacity: p.capacity || prev.capacity,
        area_capacity: p.area_capacity || prev.area_capacity,
        warehouse_area: p.warehouse_area || prev.warehouse_area
      }));
    }
  }, [formData.warehouse, formData.client_name, formData.week_number, formData.year, editEntry, getPreviousEntryData]);

  useEffect(() => {
    if (editEntry) { setFormData({ week_number: editEntry.week_number, year: editEntry.year, warehouse: editEntry.warehouse, warehouse_area: editEntry.warehouse_area || '', area_capacity: editEntry.area_capacity || 0, client_name: editEntry.client_name, opening_balance: editEntry.opening_balance || 0, inbound: editEntry.inbound || 0, outbound: editEntry.outbound || 0, closing_balance: editEntry.closing_balance || 0, capacity: editEntry.capacity || 0, uom: editEntry.uom || 'PLT', total_qty: editEntry.total_qty || 0, notes: editEntry.notes || '' }); if (editEntry.uom && !UOM_OPTIONS.includes(editEntry.uom)) setCustomUom(editEntry.uom); }
    else { setFormData({ week_number: new Date().getWeek(), year: new Date().getFullYear(), warehouse: user?.assigned_warehouse || '', warehouse_area: '', area_capacity: 0, client_name: '', opening_balance: 0, inbound: 0, outbound: 0, closing_balance: 0, capacity: 0, uom: 'PLT', total_qty: 0, notes: '' }); setCustomUom(''); }
  }, [editEntry, user, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    try {
      const submitData = { ...formData, uom: formData.uom === 'OTHER' ? customUom : formData.uom };
      const r = await fetch(editEntry ? `${AI_API_BASE}/weekly-entries/${editEntry.id}` : `${AI_API_BASE}/weekly-entries`, { method: editEntry ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(submitData) });
      const d = await r.json();
      if (d.success) { onSave(); onClose(); } else alert(d.error || 'Failed');
    } catch { alert('Error saving'); }
    setSaving(false);
  };

  if (!isOpen) return null;
  const util = formData.capacity > 0 ? Math.round((formData.closing_balance / formData.capacity) * 100) : 0;
  const availableWH = user?.role === 'admin' ? warehouses : warehouses.filter(w => w === user?.assigned_warehouse);
  const isNewWH = formData.warehouse && !warehouses.includes(formData.warehouse);
  const isNewClient = formData.client_name && !clients.includes(formData.client_name);
  // Filter warehouse areas by selected warehouse
  const filteredAreas = formData.warehouse
    ? Array.from(new Set(allEntries.filter(e => e.warehouse === formData.warehouse).map(e => e.warehouse_area).filter(Boolean)))
    : [];
  const isNewArea = formData.warehouse_area && !filteredAreas.includes(formData.warehouse_area);
  // Get ALL clients from allEntries + clients list (for selected warehouse)
  const allClients = Array.from(new Set([
    ...clients,
    ...allEntries.filter(e => !formData.warehouse || e.warehouse === formData.warehouse).map(e => e.client_name)
  ])).sort();

  return (
    <div style={styles.modalOverlay} onClick={onClose}><div style={styles.modal} onClick={(e) => e.stopPropagation()}>
      <div style={styles.modalHeader}><h2 style={styles.modalTitle}>{editEntry ? 'Edit Entry' : 'Add Entry'}</h2><button style={styles.closeBtn} onClick={onClose}>×</button></div>
      <div style={styles.modalContent}><form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={styles.inputGroup}><label style={styles.inputLabel}>Week</label><input type="number" value={formData.week_number} onChange={(e) => setFormData({ ...formData, week_number: parseInt(e.target.value) })} style={styles.input} min="1" max="53" required /></div>
          <div style={styles.inputGroup}><label style={styles.inputLabel}>Year</label><input type="number" value={formData.year} onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })} style={styles.input} required /></div>
        </div>
        <div style={styles.inputGroup}><label style={styles.inputLabel}>Warehouse {isNewWH && <span style={styles.newBadge}>NEW</span>}</label><ComboBox value={formData.warehouse} onChange={(v) => setFormData({ ...formData, warehouse: v, warehouse_area: '' })} options={availableWH} placeholder="Select warehouse" disabled={user?.role !== 'admin' && !!user?.assigned_warehouse} displayFn={getWarehouseDisplayName} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={styles.inputGroup}><label style={styles.inputLabel}>Warehouse Area {isNewArea && <span style={styles.newBadge}>NEW</span>}</label><ComboBox value={formData.warehouse_area} onChange={(v) => setFormData({ ...formData, warehouse_area: v })} options={filteredAreas} placeholder="Type to add area" /></div>
          <div style={styles.inputGroup}><label style={styles.inputLabel}>Area Capacity</label><input type="number" value={formData.area_capacity} onChange={(e) => setFormData({ ...formData, area_capacity: parseInt(e.target.value) || 0 })} style={styles.input} min="0" placeholder="Total pallet positions" /></div>
        </div>
        <div style={styles.inputGroup}><label style={styles.inputLabel}>Client {isNewClient && <span style={styles.newBadge}>NEW</span>}</label><ComboBox value={formData.client_name} onChange={(v) => setFormData({ ...formData, client_name: v })} options={allClients} placeholder="Select client" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={styles.inputGroup}><label style={styles.inputLabel}>UOM</label><select value={UOM_OPTIONS.includes(formData.uom) ? formData.uom : 'OTHER'} onChange={(e) => setFormData({ ...formData, uom: e.target.value })} style={styles.input}>{UOM_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}<option value="OTHER">Other</option></select></div>
          <div style={styles.inputGroup}><label style={styles.inputLabel}>Qty</label><input type="number" value={formData.total_qty} onChange={(e) => setFormData({ ...formData, total_qty: parseInt(e.target.value) || 0 })} style={styles.input} min="0" /></div>
        </div>
        {formData.uom === 'OTHER' && <div style={styles.inputGroup}><label style={styles.inputLabel}>Custom UOM</label><input type="text" value={customUom} onChange={(e) => setCustomUom(e.target.value.toUpperCase())} style={styles.input} maxLength={10} /></div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div style={styles.inputGroup}><label style={{ ...styles.inputLabel, color: COLORS.success }}>📥 Inbound</label><input type="number" value={formData.inbound} onChange={(e) => setFormData({ ...formData, inbound: parseInt(e.target.value) || 0 })} style={{ ...styles.input, borderColor: COLORS.success }} min="0" /></div>
          <div style={styles.inputGroup}><label style={{ ...styles.inputLabel, color: COLORS.danger }}>📤 Outbound</label><input type="number" value={formData.outbound} onChange={(e) => setFormData({ ...formData, outbound: parseInt(e.target.value) || 0 })} style={{ ...styles.input, borderColor: COLORS.danger }} min="0" /></div>
        </div>
        <div style={{ backgroundColor: '#F0FDF4', padding: '12px', borderRadius: '6px', marginBottom: '12px', border: '1px solid #86EFAC' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', textAlign: 'center' }}>
            <div><div style={{ fontSize: '10px', color: COLORS.textLight }}>Opening</div><div style={{ fontSize: '16px', fontWeight: 600 }}>{formData.opening_balance.toLocaleString()}</div></div>
            <div><div style={{ fontSize: '10px', color: COLORS.textLight }}>Net</div><div style={{ fontSize: '16px', fontWeight: 600, color: formData.inbound >= formData.outbound ? COLORS.success : COLORS.danger }}>{formData.inbound >= formData.outbound ? '+' : ''}{(formData.inbound - formData.outbound).toLocaleString()}</div></div>
            <div><div style={{ fontSize: '10px', color: COLORS.textLight }}>Closing</div><div style={{ fontSize: '18px', fontWeight: 700, color: COLORS.success }}>{formData.closing_balance.toLocaleString()}</div></div>
          </div>
          {formData.capacity > 0 && <div style={{ marginTop: '8px' }}><div style={{ height: '5px', backgroundColor: '#E5E7EB', borderRadius: '3px', overflow: 'hidden' }}><div style={{ height: '100%', width: `${Math.min(util, 100)}%`, backgroundColor: util > 90 ? COLORS.danger : util > 70 ? COLORS.warning : COLORS.success }} /></div><div style={{ fontSize: '10px', color: COLORS.textLight, marginTop: '4px', textAlign: 'center' }}>Util: {util}%</div></div>}
        </div>
        {(isNewWH || isNewClient) && <div style={{ backgroundColor: '#FFF4CE', border: '1px solid #FFE082', borderRadius: '4px', padding: '10px', marginBottom: '12px', fontSize: '11px', color: '#8A6D3B' }}>⚠️ Adding data for {isNewWH ? 'warehouse' : ''}{isNewWH && isNewClient ? ' and ' : ''}{isNewClient ? 'client' : ''} not in main database.</div>}
        <div style={styles.inputGroup}><label style={styles.inputLabel}>Notes</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} style={{ ...styles.input, minHeight: '50px', resize: 'vertical' as const }} placeholder="Optional" /></div>
        <button type="submit" style={{ ...styles.addBtn, width: '100%' }} disabled={saving}>{saving ? 'Saving...' : (editEntry ? 'Update' : 'Add Entry')}</button>
      </form></div>
    </div></div>
  );
};

// ============================================================
// QUICK MOVEMENT FORM - Simple inbound/outbound entry
// ============================================================
const QuickMovementForm: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  token: string | null;
  warehouses: string[];
  clients: string[];
  allEntries: ManualWeeklyEntry[];
  selectedWeek: number;
  selectedYear: number;
}> = ({ isOpen, onClose, onSave, token, warehouses, clients, allEntries, selectedWeek, selectedYear }) => {
  const [warehouse, setWarehouse] = useState('');
  const [client, setClient] = useState('');
  const [inbound, setInbound] = useState(0);
  const [outbound, setOutbound] = useState(0);
  const [saving, setSaving] = useState(false);

  // Get ALL unique clients from allEntries + current clients list, filtered by selected warehouse
  const availableClients = useMemo(() => {
    const clientSet = new Set<string>(clients);
    // Add all clients from manual entries for the selected warehouse
    allEntries.forEach(e => {
      if (!warehouse || e.warehouse === warehouse) {
        clientSet.add(e.client_name);
      }
    });
    return Array.from(clientSet).sort();
  }, [clients, allEntries, warehouse]);

  // Get previous week's closing balance for this warehouse/client
  const getPreviousData = useCallback(() => {
    if (!warehouse || !client) return { opening: 0, capacity: 0, area_capacity: 0, warehouse_area: '', uom: 'PLT' };

    // Find most recent entry for this warehouse/client
    const relevantEntries = allEntries.filter(e =>
      e.warehouse === warehouse && e.client_name === client &&
      (e.year < selectedYear || (e.year === selectedYear && e.week_number < selectedWeek))
    ).sort((a, b) => b.year - a.year || b.week_number - a.week_number);

    if (relevantEntries.length === 0) return { opening: 0, capacity: 0, area_capacity: 0, warehouse_area: '', uom: 'PLT' };

    const prev = relevantEntries[0];
    return {
      opening: prev.closing_balance || 0,
      capacity: prev.capacity || 0,
      area_capacity: prev.area_capacity || 0,
      warehouse_area: prev.warehouse_area || '',
      uom: prev.uom || 'PLT'
    };
  }, [warehouse, client, allEntries, selectedWeek, selectedYear]);

  const prevData = getPreviousData();
  const closing = Math.max(0, prevData.opening + inbound - outbound);
  const net = inbound - outbound;

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setWarehouse('');
      setClient('');
      setInbound(0);
      setOutbound(0);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!warehouse || !client) {
      alert('Please select warehouse and client');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        week_number: selectedWeek,
        year: selectedYear,
        warehouse,
        warehouse_area: prevData.warehouse_area || 'MAIN',
        area_capacity: prevData.area_capacity || 0,
        client_name: client,
        opening_balance: prevData.opening,
        inbound,
        outbound,
        closing_balance: closing,
        capacity: prevData.capacity || closing,
        uom: prevData.uom || 'PLT',
        total_qty: closing,
        notes: `Quick entry - Week ${selectedWeek}`
      };

      const r = await fetch(`${AI_API_BASE}/weekly-entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(submitData)
      });
      const d = await r.json();

      if (d.success) {
        onSave();
        onClose();
      } else {
        alert(d.error || 'Failed to save');
      }
    } catch (err) {
      alert('Error saving entry');
    }
    setSaving(false);
  };

  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ ...styles.modalHeader, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
          <h2 style={{ ...styles.modalTitle, color: 'white' }}>⚡ Quick Movement</h2>
          <button style={{ ...styles.closeBtn, color: 'white' }} onClick={onClose}>×</button>
        </div>

        <div style={styles.modalContent}>
          <div style={{ background: '#F0FDF4', padding: '12px', borderRadius: '8px', marginBottom: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#059669', fontWeight: 600 }}>Week {selectedWeek} - {selectedYear}</div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Warehouse */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                Warehouse
              </label>
              <select
                value={warehouse}
                onChange={(e) => { setWarehouse(e.target.value); setClient(''); }}
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '2px solid #E5E7EB', fontSize: '14px', fontWeight: 500 }}
                required
              >
                <option value="">Select warehouse...</option>
                {warehouses.map(w => <option key={w} value={w}>{getWarehouseDisplayName(w)}</option>)}
              </select>
            </div>

            {/* Client */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                Customer
              </label>
              <select
                value={client}
                onChange={(e) => setClient(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '2px solid #E5E7EB', fontSize: '14px', fontWeight: 500 }}
                required
              >
                <option value="">Select customer...</option>
                {availableClients.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Previous balance info */}
            {warehouse && client && (
              <div style={{ background: '#F3F4F6', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '4px' }}>Previous Week Closing</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#1F2937' }}>{prevData.opening.toLocaleString()} <span style={{ fontSize: '12px', color: '#6B7280' }}>pallets</span></div>
              </div>
            )}

            {/* Inbound / Outbound */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#10B981', marginBottom: '6px' }}>
                  📥 Inbound
                </label>
                <input
                  type="number"
                  value={inbound || ''}
                  onChange={(e) => setInbound(parseInt(e.target.value) || 0)}
                  style={{ width: '100%', padding: '14px', borderRadius: '8px', border: '2px solid #10B981', fontSize: '18px', fontWeight: 600, textAlign: 'center', boxSizing: 'border-box' }}
                  min="0"
                  placeholder="0"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#EF4444', marginBottom: '6px' }}>
                  📤 Outbound
                </label>
                <input
                  type="number"
                  value={outbound || ''}
                  onChange={(e) => setOutbound(parseInt(e.target.value) || 0)}
                  style={{ width: '100%', padding: '14px', borderRadius: '8px', border: '2px solid #EF4444', fontSize: '18px', fontWeight: 600, textAlign: 'center', boxSizing: 'border-box' }}
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Result */}
            <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', padding: '16px', borderRadius: '12px', marginBottom: '16px', color: 'white' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', textAlign: 'center' }}>
                <div>
                  <div style={{ fontSize: '10px', opacity: 0.8 }}>Opening</div>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>{prevData.opening.toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: '10px', opacity: 0.8 }}>Net</div>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>{net >= 0 ? '+' : ''}{net.toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: '10px', opacity: 0.8 }}>Closing</div>
                  <div style={{ fontSize: '24px', fontWeight: 800 }}>{closing.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={saving || !warehouse || !client}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '8px',
                border: 'none',
                background: saving || !warehouse || !client ? '#9CA3AF' : 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                color: 'white',
                fontSize: '16px',
                fontWeight: 700,
                cursor: saving || !warehouse || !client ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? '⏳ Saving...' : '✓ Save Entry'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// CLIENT DRILL-DOWN MODAL
// ============================================================
const ClientDrillDownModal: React.FC<{ isOpen: boolean; onClose: () => void; client: string | null; warehouse: string | null; source?: 'IBM' | 'MANUAL' | 'MERGED'; manualEntries?: ManualWeeklyEntry[]; }> = ({ isOpen, onClose, client, warehouse, source, manualEntries = [] }) => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<ClientSummary | null>(null);
  const [uomData, setUomData] = useState<UOMData[]>([]);
  const [items, setItems] = useState<ItemData[]>([]);
  const [view, setView] = useState<'uom' | 'items'>('uom');

  const manualUomData = useMemo(() => {
    if (!client) return [];
    const entries = manualEntries.filter(e => e.client_name === client && (!warehouse || e.warehouse === warehouse));
    const m = new Map<string, { p: number; q: number; c: number }>();
    entries.forEach(e => { const u = e.uom || 'PLT'; if (!m.has(u)) m.set(u, { p: 0, q: 0, c: 0 }); const d = m.get(u)!; d.p += e.closing_balance || 0; d.q += e.total_qty || 0; d.c++; });
    return Array.from(m.entries()).map(([u, d]) => ({ UOM: u, PALLET_COUNT: d.p, TOTAL_QTY: d.q, RESERVED_QTY: 0, ITEM_COUNT: d.c, AREA_COUNT: 1 }));
  }, [client, warehouse, manualEntries]);

  const manualSummary = useMemo(() => {
    if (!client) return null;
    const entries = manualEntries.filter(e => e.client_name === client && (!warehouse || e.warehouse === warehouse));
    if (entries.length === 0) return null;
    return { TOTAL_PALLETS: entries.reduce((s, e) => s + (e.closing_balance || 0), 0), UOM_COUNT: new Set(entries.map(e => e.uom || 'PLT')).size, TOTAL_ITEMS: entries.length, TOTAL_AREAS: 1, WAREHOUSE_COUNT: new Set(entries.map(e => e.warehouse)).size };
  }, [client, warehouse, manualEntries]);

  const fetchUOMData = useCallback(async () => {
    if (!client || source === 'MANUAL') return;
    setLoading(true);
    try {
      const p = new URLSearchParams(); p.set('client', client.trim()); if (warehouse) p.set('warehouse', warehouse);
      const r = await fetch(`${API_BASE}/utilization/customer-uom-breakdown?${p.toString()}`);
      const d = await r.json();
      if (d.success) { setSummary(d.summary); setUomData(d.data || []); }
    } catch { }
    setLoading(false);
  }, [client, warehouse, source]);

  const fetchItems = useCallback(async (uom?: string) => {
    if (!client) return;
    setLoading(true);
    try {
      const p = new URLSearchParams(); p.set('client', client.trim()); p.set('limit', '100'); if (warehouse) p.set('warehouse', warehouse); if (uom) p.set('uom', uom);
      const r = await fetch(`${API_BASE}/utilization/customer-items?${p.toString()}`);
      const d = await r.json();
      if (d.success) { setItems(d.data || []); setView('items'); }
    } catch { }
    setLoading(false);
  }, [client, warehouse]);

  useEffect(() => { if (isOpen && client) { setView('uom'); setItems([]); setSummary(null); setUomData([]); fetchUOMData(); } }, [isOpen, client, fetchUOMData]);

  if (!isOpen) return null;
  const dSum = source === 'MANUAL' ? manualSummary : summary;
  const dUom = source === 'MANUAL' ? manualUomData : uomData;

  return (
    <div style={styles.modalOverlay} onClick={onClose}><div style={styles.largeModal} onClick={(e) => e.stopPropagation()}>
      <div style={styles.modalHeader}><h2 style={styles.modalTitle}>📦 {client} <SourceBadge source={source} /></h2><button style={styles.closeBtn} onClick={onClose}>×</button></div>
      <div style={styles.modalContent}>
        {loading ? <div style={{ textAlign: 'center', padding: '30px', color: COLORS.textLight }}>Loading...</div> : <>
          {dSum && <div style={styles.summaryGrid}><div style={styles.summaryCard}><div style={styles.summaryValue}>{dSum.TOTAL_PALLETS?.toLocaleString() || 0}</div><div style={styles.summaryLabel}>Pallets</div></div><div style={styles.summaryCard}><div style={{ ...styles.summaryValue, color: COLORS.success }}>{dSum.UOM_COUNT || 0}</div><div style={styles.summaryLabel}>UOMs</div></div><div style={styles.summaryCard}><div style={{ ...styles.summaryValue, color: COLORS.accent }}>{dSum.TOTAL_ITEMS || 0}</div><div style={styles.summaryLabel}>{source === 'MANUAL' ? 'Entries' : 'Items'}</div></div><div style={styles.summaryCard}><div style={{ ...styles.summaryValue, color: '#7B68EE' }}>{dSum.WAREHOUSE_COUNT || dSum.TOTAL_AREAS || 0}</div><div style={styles.summaryLabel}>{source === 'MANUAL' ? 'WHs' : 'Areas'}</div></div></div>}
          {view === 'items' && source !== 'MANUAL' && <button style={styles.backBtn} onClick={() => { setView('uom'); setItems([]); }}>← Back</button>}
          {view === 'uom' && dUom.length > 0 && <>
            <div style={{ height: '200px', marginBottom: '14px' }}><ResponsiveContainer width="100%" height="100%"><BarChart data={dUom} layout="vertical" margin={{ left: 40 }}><CartesianGrid strokeDasharray="3 3" horizontal vertical={false} /><XAxis type="number" tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="UOM" tick={{ fontSize: 10 }} width={35} /><Tooltip /><Bar dataKey="PALLET_COUNT" radius={[0, 4, 4, 0]} cursor={source !== 'MANUAL' ? 'pointer' : 'default'} onClick={(d: any) => source !== 'MANUAL' && d?.UOM && fetchItems(d.UOM)}>{dUom.map((_, i) => <Cell key={i} fill={UOM_COLORS[i % UOM_COLORS.length]} />)}</Bar></BarChart></ResponsiveContainer></div>
            <table style={styles.itemsTable}><thead><tr><th style={styles.itemsTh}>UOM</th><th style={styles.itemsTh}>Pallets</th><th style={styles.itemsTh}>Qty</th><th style={styles.itemsTh}>{source === 'MANUAL' ? 'Entries' : 'Items'}</th></tr></thead><tbody>{dUom.map((r, i) => <tr key={r.UOM} style={{ backgroundColor: i % 2 === 0 ? 'white' : COLORS.lightGray, cursor: source !== 'MANUAL' ? 'pointer' : 'default' }} onClick={() => source !== 'MANUAL' && fetchItems(r.UOM)}><td style={styles.itemsTd}><span style={{ padding: '2px 6px', backgroundColor: UOM_COLORS[i % UOM_COLORS.length] + '20', color: UOM_COLORS[i % UOM_COLORS.length], borderRadius: '3px', fontWeight: 600, fontSize: '10px' }}>{r.UOM}</span></td><td style={styles.itemsTd}><strong>{r.PALLET_COUNT?.toLocaleString()}</strong></td><td style={styles.itemsTd}>{r.TOTAL_QTY?.toLocaleString() || '-'}</td><td style={styles.itemsTd}>{r.ITEM_COUNT}</td></tr>)}</tbody></table>
          </>}
          {view === 'items' && items.length > 0 && <div style={{ maxHeight: '300px', overflowY: 'auto' }}><table style={styles.itemsTable}><thead style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}><tr><th style={styles.itemsTh}>Code</th><th style={styles.itemsTh}>Name</th><th style={styles.itemsTh}>UOM</th><th style={styles.itemsTh}>Pallets</th><th style={styles.itemsTh}>Qty</th></tr></thead><tbody>{items.map((it, i) => <tr key={`${it.ITEM_CODE}-${i}`} style={{ backgroundColor: i % 2 === 0 ? 'white' : COLORS.lightGray }}><td style={styles.itemsTd}><strong>{it.ITEM_CODE}</strong></td><td style={styles.itemsTd}>{it.ITEM_NAME?.substring(0, 20) || '-'}</td><td style={styles.itemsTd}>{it.UOM}</td><td style={styles.itemsTd}><strong>{it.PALLET_COUNT?.toLocaleString()}</strong></td><td style={styles.itemsTd}>{it.AVAILABLE_QTY?.toLocaleString()}</td></tr>)}</tbody></table></div>}
        </>}
      </div>
    </div></div>
  );
};

// ============================================================
// TRENDS TAB - Monthly Utilization by Customer
// ============================================================
const TREND_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#6366f1', '#14b8a6', '#f97316'];

interface MovementTrendData {
  customer: string;
  totalInbound: number;
  totalOutbound: number;
  totalInPallets: number;
  totalOutPallets: number;
  netMovement: number;
  netPallets: number;
  periods: { period: number; inbound: number; outbound: number; inPallets: number; outPallets: number; net: number; netPallets: number }[];
}

interface PeriodTotal {
  period: number;
  periodLabel: string;
  inbound: number;
  outbound: number;
  inPallets: number;
  outPallets: number;
  net: number;
  netPallets: number;
}

const TrendsTab: React.FC<{ weeklyEntries: ManualWeeklyEntry[]; selectedWarehouse: string | null; selectedYear: number; token?: string | null; }> = ({ weeklyEntries, selectedWarehouse, selectedYear, token }) => {
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [chartType, setChartType] = useState<'line' | 'bar' | 'area'>('area');
  const [viewMode, setViewMode] = useState<'pallets' | 'movement'>('pallets');
  const [db2Customers, setDb2Customers] = useState<{ CLIENT: string; CURRENT_PALLETS: number; WAREHOUSE?: string }[]>([]);
  const [loadingDb2, setLoadingDb2] = useState(false);
  const [db2MovementTrends, setDb2MovementTrends] = useState<MovementTrendData[]>([]);
  const [db2PeriodTotals, setDb2PeriodTotals] = useState<PeriodTotal[]>([]);
  const [loadingMovement, setLoadingMovement] = useState(false);

  // Fetch current DB2 customer data
  useEffect(() => {
    const fetchDb2Data = async () => {
      setLoadingDb2(true);
      try {
        let url = `${API_BASE}/utilization/top-clients-comparison?limit=500`;
        if (selectedWarehouse) url += `&warehouse=${selectedWarehouse}`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.success && data.data) {
          setDb2Customers(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch DB2 customers:', err);
      }
      setLoadingDb2(false);
    };
    fetchDb2Data();
  }, [selectedWarehouse]);

  // Fetch DB2 movement trends (inbound/outbound)
  useEffect(() => {
    const fetchMovementTrends = async () => {
      setLoadingMovement(true);
      try {
        let url = `${AI_API_BASE}/movement-trends?year=${selectedYear}&period=month`;
        if (selectedWarehouse) url += `&warehouse=${selectedWarehouse}`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.success) {
          setDb2MovementTrends(data.customers || []);
          setDb2PeriodTotals(data.periodTotals || []);
        }
      } catch (err) {
        console.error('Failed to fetch movement trends:', err);
      }
      setLoadingMovement(false);
    };
    fetchMovementTrends();
  }, [selectedWarehouse, selectedYear]);

  // Get all unique customers from entries AND DB2
  const allCustomers = useMemo(() => {
    const customers = new Set<string>();
    // Add from manual entries
    weeklyEntries.forEach(e => {
      if (!selectedWarehouse || e.warehouse === selectedWarehouse) {
        customers.add(e.client_name);
      }
    });
    // Add from DB2
    db2Customers.forEach(c => {
      customers.add(c.CLIENT);
    });
    return Array.from(customers).sort();
  }, [weeklyEntries, selectedWarehouse, db2Customers]);

  // Auto-select top 5 customers if none selected
  useEffect(() => {
    if (selectedCustomers.length === 0 && allCustomers.length > 0) {
      // Get top 5 by total closing balance
      const customerTotals = new Map<string, number>();
      weeklyEntries.forEach(e => {
        if (!selectedWarehouse || e.warehouse === selectedWarehouse) {
          customerTotals.set(e.client_name, (customerTotals.get(e.client_name) || 0) + (e.closing_balance || 0));
        }
      });
      const top5 = Array.from(customerTotals.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([name]) => name);
      setSelectedCustomers(top5);
    }
  }, [allCustomers, weeklyEntries, selectedWarehouse, selectedCustomers.length]);

  // Calculate monthly trend data by customer
  const monthlyTrendData = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyData: { [month: string]: { [customer: string]: { pallets: number; capacity: number; count: number } } } = {};

    // Initialize all months
    monthNames.forEach(m => {
      monthlyData[m] = {};
    });

    // Aggregate weekly entries by month
    weeklyEntries.forEach(entry => {
      if (entry.year !== selectedYear) return;
      if (selectedWarehouse && entry.warehouse !== selectedWarehouse) return;
      if (selectedCustomers.length > 0 && !selectedCustomers.includes(entry.client_name)) return;

      // Calculate month from week number (approximate)
      const monthIndex = Math.min(11, Math.floor((entry.week_number - 1) / 4.33));
      const month = monthNames[monthIndex];

      if (!monthlyData[month][entry.client_name]) {
        monthlyData[month][entry.client_name] = { pallets: 0, capacity: 0, count: 0 };
      }

      monthlyData[month][entry.client_name].pallets += entry.closing_balance || 0;
      monthlyData[month][entry.client_name].capacity += entry.capacity || 0;
      monthlyData[month][entry.client_name].count++;
    });

    // Convert to chart format
    return monthNames.map(month => {
      const row: any = { month };
      selectedCustomers.forEach(customer => {
        const data = monthlyData[month][customer];
        if (data && data.count > 0) {
          // Average pallets for the month
          row[customer] = Math.round(data.pallets / data.count);
          row[`${customer}_util`] = data.capacity > 0 ? Math.round((data.pallets / data.capacity) * 100 / data.count) : 0;
        } else {
          row[customer] = 0;
          row[`${customer}_util`] = 0;
        }
      });
      return row;
    });
  }, [weeklyEntries, selectedYear, selectedWarehouse, selectedCustomers]);

  // Calculate monthly inbound/outbound movement data by customer
  const movementTrendData = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyData: { [month: string]: { [customer: string]: { inbound: number; outbound: number; count: number } } } = {};

    // Initialize all months
    monthNames.forEach(m => {
      monthlyData[m] = {};
    });

    // Aggregate weekly entries by month
    weeklyEntries.forEach(entry => {
      if (entry.year !== selectedYear) return;
      if (selectedWarehouse && entry.warehouse !== selectedWarehouse) return;
      if (selectedCustomers.length > 0 && !selectedCustomers.includes(entry.client_name)) return;

      const monthIndex = Math.min(11, Math.floor((entry.week_number - 1) / 4.33));
      const month = monthNames[monthIndex];

      if (!monthlyData[month][entry.client_name]) {
        monthlyData[month][entry.client_name] = { inbound: 0, outbound: 0, count: 0 };
      }

      monthlyData[month][entry.client_name].inbound += entry.inbound || 0;
      monthlyData[month][entry.client_name].outbound += entry.outbound || 0;
      monthlyData[month][entry.client_name].count++;
    });

    // Convert to chart format
    return monthNames.map(month => {
      const row: any = { month };
      selectedCustomers.forEach(customer => {
        const data = monthlyData[month][customer];
        if (data && data.count > 0) {
          row[`${customer}_in`] = data.inbound;
          row[`${customer}_out`] = data.outbound;
          row[`${customer}_net`] = data.inbound - data.outbound;
        } else {
          row[`${customer}_in`] = 0;
          row[`${customer}_out`] = 0;
          row[`${customer}_net`] = 0;
        }
      });
      // Totals for all selected customers
      let totalIn = 0, totalOut = 0;
      selectedCustomers.forEach(customer => {
        totalIn += row[`${customer}_in`] || 0;
        totalOut += row[`${customer}_out`] || 0;
      });
      row.totalInbound = totalIn;
      row.totalOutbound = totalOut;
      row.netMovement = totalIn - totalOut;
      return row;
    });
  }, [weeklyEntries, selectedYear, selectedWarehouse, selectedCustomers]);

  // Calculate customer summary stats with inbound/outbound
  const customerSummary = useMemo(() => {
    const summary: { customer: string; totalPallets: number; avgPallets: number; avgUtil: number; entries: number; totalInbound: number; totalOutbound: number; netMovement: number }[] = [];

    selectedCustomers.forEach(customer => {
      const entries = weeklyEntries.filter(e =>
        e.client_name === customer &&
        e.year === selectedYear &&
        (!selectedWarehouse || e.warehouse === selectedWarehouse)
      );

      if (entries.length > 0) {
        const totalPallets = entries.reduce((sum, e) => sum + (e.closing_balance || 0), 0);
        const totalCapacity = entries.reduce((sum, e) => sum + (e.capacity || 0), 0);
        const totalInbound = entries.reduce((sum, e) => sum + (e.inbound || 0), 0);
        const totalOutbound = entries.reduce((sum, e) => sum + (e.outbound || 0), 0);
        summary.push({
          customer,
          totalPallets,
          avgPallets: Math.round(totalPallets / entries.length),
          avgUtil: totalCapacity > 0 ? Math.round((totalPallets / totalCapacity) * 100) : 0,
          entries: entries.length,
          totalInbound,
          totalOutbound,
          netMovement: totalInbound - totalOutbound
        });
      }
    });

    return summary.sort((a, b) => b.avgPallets - a.avgPallets);
  }, [weeklyEntries, selectedYear, selectedWarehouse, selectedCustomers]);

  const toggleCustomer = (customer: string) => {
    setSelectedCustomers(prev =>
      prev.includes(customer)
        ? prev.filter(c => c !== customer)
        : [...prev, customer]
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white'
      }}>
        <h2 style={{ fontSize: '24px', fontWeight: 800, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '32px' }}>📈</span>
          Utilization Trends by Customer
        </h2>
        <p style={{ fontSize: '14px', opacity: 0.9 }}>
          Monthly trend analysis for {selectedYear} {selectedWarehouse ? `- ${selectedWarehouse}` : '- All Warehouses'}
        </p>
      </div>

      {/* Controls */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        alignItems: 'center'
      }}>
        <div>
          <label style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '6px', display: 'block' }}>View Mode</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setViewMode('pallets')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: viewMode === 'pallets' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#f1f5f9',
                color: viewMode === 'pallets' ? 'white' : '#64748b',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              📦 Pallets
            </button>
            <button
              onClick={() => setViewMode('movement')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: viewMode === 'movement' ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' : '#f1f5f9',
                color: viewMode === 'movement' ? 'white' : '#64748b',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              🔄 Inbound/Outbound
            </button>
          </div>
        </div>

        <div>
          <label style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '6px', display: 'block' }}>Chart Type</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {(['area', 'line', 'bar'] as const).map(type => (
              <button
                key={type}
                onClick={() => setChartType(type)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: chartType === type ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : '#f1f5f9',
                  color: chartType === type ? 'white' : '#64748b',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  textTransform: 'capitalize'
                }}
              >
                {type === 'area' ? '📊 Area' : type === 'line' ? '📈 Line' : '📊 Bar'}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '6px', display: 'block' }}>
            Select Customers ({selectedCustomers.length} selected)
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '80px', overflowY: 'auto' }}>
            {allCustomers.slice(0, 20).map((customer, idx) => (
              <button
                key={customer}
                onClick={() => toggleCustomer(customer)}
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  border: selectedCustomers.includes(customer) ? 'none' : '1px solid #e2e8f0',
                  background: selectedCustomers.includes(customer) ? TREND_COLORS[idx % TREND_COLORS.length] : 'white',
                  color: selectedCustomers.includes(customer) ? 'white' : '#64748b',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {customer.length > 15 ? customer.substring(0, 15) + '...' : customer}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Trend Chart - Pallets View */}
      {viewMode === 'pallets' && <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '20px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '24px' }}>📦</span>
          Monthly Pallet Trend by Customer
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          {chartType === 'area' ? (
            <AreaChart data={monthlyTrendData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
              <Tooltip
                contentStyle={{ background: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                formatter={(value: number) => [value.toLocaleString() + ' pallets', '']}
              />
              <Legend />
              {selectedCustomers.map((customer, idx) => (
                <Area
                  key={customer}
                  type="monotone"
                  dataKey={customer}
                  name={customer}
                  stroke={TREND_COLORS[idx % TREND_COLORS.length]}
                  fill={TREND_COLORS[idx % TREND_COLORS.length]}
                  fillOpacity={0.3}
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          ) : chartType === 'line' ? (
            <ComposedChart data={monthlyTrendData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
              <Tooltip
                contentStyle={{ background: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                formatter={(value: number) => [value.toLocaleString() + ' pallets', '']}
              />
              <Legend />
              {selectedCustomers.map((customer, idx) => (
                <Line
                  key={customer}
                  type="monotone"
                  dataKey={customer}
                  name={customer}
                  stroke={TREND_COLORS[idx % TREND_COLORS.length]}
                  strokeWidth={3}
                  dot={{ fill: TREND_COLORS[idx % TREND_COLORS.length], strokeWidth: 2, r: 4 }}
                />
              ))}
            </ComposedChart>
          ) : (
            <BarChart data={monthlyTrendData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
              <Tooltip
                contentStyle={{ background: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                formatter={(value: number) => [value.toLocaleString() + ' pallets', '']}
              />
              <Legend />
              {selectedCustomers.map((customer, idx) => (
                <Bar
                  key={customer}
                  dataKey={customer}
                  name={customer}
                  fill={TREND_COLORS[idx % TREND_COLORS.length]}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>}

      {/* Main Trend Chart - Movement View (Inbound/Outbound) from DB2 */}
      {viewMode === 'movement' && <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '20px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '24px' }}>🔄</span>
          Monthly Inbound vs Outbound Movement (DB2)
          {loadingMovement && <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 400 }}>Loading...</span>}
        </h3>

        {db2PeriodTotals.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={db2PeriodTotals} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="periodLabel" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
              <Tooltip
                contentStyle={{ background: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                formatter={(value: number, name: string) => [value.toLocaleString() + ' qty', name]}
              />
              <Legend />
              <Bar dataKey="inbound" name="Inbound (Qty)" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="outbound" name="Outbound (Qty)" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="net" name="Net Movement" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5 }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
            {loadingMovement ? 'Loading movement data...' : 'No movement data available'}
          </div>
        )}

        {/* Top Customers by Movement */}
        {db2MovementTrends.length > 0 && (
          <>
            <h4 style={{ fontSize: '16px', fontWeight: 700, margin: '24px 0 16px 0', color: '#64748b' }}>Top Customers by Movement</h4>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={db2MovementTrends.slice(0, 10).map((c, i) => ({
                  name: c.customer.length > 12 ? c.customer.substring(0, 12) + '...' : c.customer,
                  fullName: c.customer,
                  inbound: c.totalInbound,
                  outbound: c.totalOutbound,
                  inPallets: c.totalInPallets,
                  outPallets: c.totalOutPallets,
                  net: c.netMovement,
                  color: TREND_COLORS[i % TREND_COLORS.length]
                }))}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} width={100} />
                <Tooltip
                  contentStyle={{ background: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                  formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                  labelFormatter={(label: string, payload: readonly any[]) => payload[0]?.payload?.fullName || label}
                />
                <Legend />
                <Bar dataKey="inbound" name="Inbound" fill="#10b981" radius={[0, 4, 4, 0]} />
                <Bar dataKey="outbound" name="Outbound" fill="#ef4444" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}

        {/* Manual Entry Movement Data (if available) */}
        {movementTrendData.some(d => d.totalInbound > 0 || d.totalOutbound > 0) && (
          <>
            <h4 style={{ fontSize: '16px', fontWeight: 700, margin: '24px 0 16px 0', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px', fontSize: '10px' }}>MANUAL</span>
              Weekly Entry Data
            </h4>
            <ResponsiveContainer width="100%" height={250}>
              <ComposedChart data={movementTrendData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                <Tooltip
                  contentStyle={{ background: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                  formatter={(value: number, name: string) => [value.toLocaleString() + ' pallets', name]}
                />
                <Legend />
                <Bar dataKey="totalInbound" name="Total Inbound" fill="#10b981" fillOpacity={0.6} radius={[4, 4, 0, 0]} />
                <Bar dataKey="totalOutbound" name="Total Outbound" fill="#ef4444" fillOpacity={0.6} radius={[4, 4, 0, 0]} />
                <Line type="monotone" dataKey="netMovement" name="Net Movement" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: '#3b82f6', r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </>
        )}
      </div>}

      {/* Customer Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {customerSummary.map((item, idx) => (
          <div key={item.customer} style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            borderLeft: `4px solid ${TREND_COLORS[idx % TREND_COLORS.length]}`
          }}>
            <div style={{ fontSize: '14px', fontWeight: 800, color: '#1e293b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: TREND_COLORS[idx % TREND_COLORS.length]
              }} />
              {item.customer}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, marginBottom: '2px' }}>AVG PALLETS</div>
                <div style={{ fontSize: '20px', fontWeight: 900, color: TREND_COLORS[idx % TREND_COLORS.length] }}>
                  {item.avgPallets.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, marginBottom: '2px' }}>AVG UTIL</div>
                <div style={{ fontSize: '20px', fontWeight: 900, color: item.avgUtil > 80 ? '#ef4444' : item.avgUtil > 60 ? '#f59e0b' : '#10b981' }}>
                  {item.avgUtil}%
                </div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, marginBottom: '2px' }}>TOTAL INBOUND</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontSize: '10px' }}>▲</span> {item.totalInbound.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, marginBottom: '2px' }}>TOTAL OUTBOUND</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontSize: '10px' }}>▼</span> {item.totalOutbound.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, marginBottom: '2px' }}>NET MOVEMENT</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: item.netMovement >= 0 ? '#3b82f6' : '#f59e0b' }}>
                  {item.netMovement >= 0 ? '+' : ''}{item.netMovement.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700, marginBottom: '2px' }}>ENTRIES</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#64748b' }}>
                  {item.entries}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* DB2 Current State - Real-time from IBM */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '20px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '24px' }}>🔴</span>
          Current State from DB2 (Real-time)
          {loadingDb2 && <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 400 }}>Loading...</span>}
        </h3>

        {db2Customers.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={db2Customers.slice(0, 15).map((c, i) => ({
                name: c.CLIENT.length > 12 ? c.CLIENT.substring(0, 12) + '...' : c.CLIENT,
                fullName: c.CLIENT,
                pallets: c.CURRENT_PALLETS,
                color: TREND_COLORS[i % TREND_COLORS.length]
              }))}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 11, fill: '#334155', fontWeight: 600 }}
                width={95}
              />
              <Tooltip
                contentStyle={{ background: 'white', borderRadius: '12px', border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}
                formatter={(value: number, name: string, props: any) => [value.toLocaleString() + ' pallets', props.payload.fullName]}
              />
              <Bar
                dataKey="pallets"
                name="Current Pallets"
                radius={[0, 4, 4, 0]}
              >
                {db2Customers.slice(0, 15).map((_, index) => (
                  <Cell key={`cell-${index}`} fill={TREND_COLORS[index % TREND_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
            {loadingDb2 ? 'Loading DB2 data...' : 'No DB2 data available'}
          </div>
        )}

        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: '#fef3c7',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontSize: '20px' }}>💡</span>
          <div style={{ fontSize: '12px', color: '#92400e' }}>
            <strong>Note:</strong> DB2 shows current state only. For historical trends, use weekly entries data which is captured and stored each week.
          </div>
        </div>
      </div>

      {/* No Data Message */}
      {selectedCustomers.length === 0 && (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          color: '#64748b'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <div style={{ fontSize: '16px', fontWeight: 700 }}>Select customers to view trends</div>
          <div style={{ fontSize: '13px', marginTop: '8px' }}>Click on customer buttons above to compare their utilization over time</div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// USER MANAGEMENT PANEL
// ============================================================
const UserManagementPanel: React.FC<{ token: string | null; warehouses: string[]; }> = ({ token, warehouses }) => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editUser, setEditUser] = useState<UserData | null>(null);
  const [formData, setFormData] = useState<{ username: string; password: string; display_name: string; role: 'admin' | 'warehouse_supervisor'; assigned_warehouse: string; email: string }>({ username: '', password: '', display_name: '', role: 'warehouse_supervisor', assigned_warehouse: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try { const r = await fetch(`${API_BASE}/auth/users`, { headers: { 'Authorization': `Bearer ${token}` } }); const d = await r.json(); if (d.success) setUsers(d.data || []); } catch { }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(null); setSaving(true);
    if (!formData.username || (!editUser && !formData.password)) { setError('Username and password required'); setSaving(false); return; }
    if (formData.role === 'warehouse_supervisor' && !formData.assigned_warehouse) { setError('Warehouse required'); setSaving(false); return; }
    try {
      const body = editUser ? { ...formData, password: formData.password || undefined } : formData;
      const r = await fetch(editUser ? `${API_BASE}/auth/users/${editUser.id}` : `${API_BASE}/auth/users`, { method: editUser ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify(body) });
      const d = await r.json();
      if (d.success) { setShowAddModal(false); setEditUser(null); setFormData({ username: '', password: '', display_name: '', role: 'warehouse_supervisor', assigned_warehouse: '', email: '' }); fetchUsers(); }
      else setError(d.error || 'Failed');
    } catch { setError('Network error'); }
    setSaving(false);
  };

  const handleToggleActive = async (id: number, active: boolean) => {
    if (!window.confirm(`${active ? 'Deactivate' : 'Activate'} user?`)) return;
    try { const r = await fetch(`${API_BASE}/auth/users/${id}/toggle`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, body: JSON.stringify({ is_active: !active }) }); const d = await r.json(); if (d.success) fetchUsers(); } catch { }
  };

  return (
    <div style={styles.fullWidthCard}>
      <h3 style={styles.chartTitle}><span>👥 User Management</span><button style={styles.addBtn} onClick={() => { setEditUser(null); setFormData({ username: '', password: '', display_name: '', role: 'warehouse_supervisor', assigned_warehouse: '', email: '' }); setShowAddModal(true); }}>+ Add User</button></h3>
      {loading ? <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textLight }}><div style={{ fontSize: '32px', marginBottom: '10px' }}>⏳</div>Loading users...</div> : (
        <div style={{ overflowX: 'auto' }}><table style={styles.table}><thead><tr><th style={styles.th}>User</th><th style={styles.th}>Role</th><th style={styles.th}>Warehouse</th><th style={styles.th}>Status</th><th style={styles.th}>Actions</th></tr></thead><tbody>
          {users.map(u => <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}><td style={styles.td}><strong>{u.username}</strong><br /><span style={{ fontSize: '10px', color: COLORS.textLight }}>{u.display_name || '-'}</span></td><td style={styles.td}><span style={{ padding: '2px 8px', borderRadius: '3px', backgroundColor: u.role === 'admin' ? '#E8F4FD' : '#F3E5F5', color: u.role === 'admin' ? COLORS.primary : COLORS.secondary, fontWeight: 600, fontSize: '10px' }}>{u.role === 'admin' ? 'Admin' : 'Supervisor'}</span></td><td style={styles.td}>{u.assigned_warehouse ? getWarehouseDisplayName(u.assigned_warehouse) : '-'}</td><td style={styles.td}><span style={{ padding: '2px 8px', borderRadius: '3px', backgroundColor: u.is_active ? '#C6EFCE' : '#FFC7CE', color: u.is_active ? COLORS.success : COLORS.danger, fontWeight: 600, fontSize: '10px' }}>{u.is_active ? 'Active' : 'Inactive'}</span></td><td style={styles.td}><button style={{ ...styles.actionBtn, backgroundColor: '#E8F4FD', color: COLORS.primary }} onClick={() => { setEditUser(u); setFormData({ username: u.username, password: '', display_name: u.display_name || '', role: u.role, assigned_warehouse: u.assigned_warehouse || '', email: u.email || '' }); setShowAddModal(true); }}>Edit</button><button style={{ ...styles.actionBtn, backgroundColor: u.is_active ? '#FFC7CE' : '#C6EFCE', color: u.is_active ? COLORS.danger : COLORS.success }} onClick={() => handleToggleActive(u.id, u.is_active)}>{u.is_active ? 'Off' : 'On'}</button></td></tr>)}
        </tbody></table></div>
      )}
      {showAddModal && <div style={styles.modalOverlay} onClick={() => setShowAddModal(false)}><div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}><h2 style={styles.modalTitle}>{editUser ? 'Edit User' : 'Add User'}</h2><button style={styles.closeBtn} onClick={() => setShowAddModal(false)}>×</button></div>
        <div style={styles.modalContent}><form onSubmit={handleSubmit}>
          {error && <div style={{ backgroundColor: '#FEE2E2', color: COLORS.danger, padding: '10px', borderRadius: '4px', marginBottom: '12px', fontSize: '12px' }}>⚠️ {error}</div>}
          <div style={styles.inputGroup}><label style={styles.inputLabel}>Username *</label><input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s/g, '_') })} style={styles.input} disabled={!!editUser} required /></div>
          <div style={styles.inputGroup}><label style={styles.inputLabel}>Password {editUser && '(blank = keep)'}</label><input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} style={styles.input} required={!editUser} minLength={6} /></div>
          <div style={styles.inputGroup}><label style={styles.inputLabel}>Display Name</label><input type="text" value={formData.display_name} onChange={(e) => setFormData({ ...formData, display_name: e.target.value })} style={styles.input} /></div>
          <div style={styles.inputGroup}><label style={styles.inputLabel}>Role *</label><select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'warehouse_supervisor', assigned_warehouse: e.target.value === 'admin' ? '' : formData.assigned_warehouse })} style={styles.input}><option value="warehouse_supervisor">Supervisor</option><option value="admin">Admin</option></select></div>
          {formData.role === 'warehouse_supervisor' && <div style={styles.inputGroup}><label style={styles.inputLabel}>Warehouse *</label><select value={formData.assigned_warehouse} onChange={(e) => setFormData({ ...formData, assigned_warehouse: e.target.value })} style={styles.input} required><option value="">-- Select --</option>{warehouses.map(w => <option key={w} value={w}>{getWarehouseDisplayName(w)}</option>)}</select></div>}
          <button type="submit" style={{ ...styles.addBtn, width: '100%' }} disabled={saving}>{saving ? 'Saving...' : (editUser ? 'Update' : 'Create')}</button>
        </form></div>
      </div></div>}
    </div>
  );
};

// ============================================================
// MAIN DASHBOARD - COMPACT WITH FIXED SIDEBAR
// ============================================================
const UtilizationDashboard: React.FC = () => {
  // Modern theme system
  const { theme, colors } = useTheme();

  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'utilization' | 'bins' | 'weekly' | 'trends' | 'users' | '3d'>('utilization');
  const [rawWarehouseData, setRawWarehouseData] = useState<RawWarehouseItem[]>([]);
  const [rawVirtualData, setRawVirtualData] = useState<RawWarehouseItem[]>([]);
  const [customerStorageData, setCustomerStorageData] = useState<CustomerStorageItem[]>([]);
  const [warehouseMappings, setWarehouseMappings] = useState<WarehouseMapping[]>([]);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseData[]>([]);
  const [topClients, setTopClients] = useState<ClientData[]>([]);
  const [clientsList, setClientsList] = useState<string[]>([]);
  const [weeklyEntries, setWeeklyEntries] = useState<ManualWeeklyEntry[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string | null>(null);
  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<number>(new Date().getWeek());
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [selectedClientSource, setSelectedClientSource] = useState<'IBM' | 'MANUAL' | 'MERGED'>('IBM');
  const [showClientModal, setShowClientModal] = useState(false);
  const [dataSource, setDataSource] = useState<'SNAPSHOT' | 'REALTIME'>('REALTIME');
  const [snapshotDate, setSnapshotDate] = useState<string | null>(null);
  const [showWeeklyEntry, setShowWeeklyEntry] = useState(false);
  const [showQuickMovement, setShowQuickMovement] = useState(false);
  const [showTeamsSettings, setShowTeamsSettings] = useState(false);
  const [editEntry, setEditEntry] = useState<ManualWeeklyEntry | null>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [cacheLoaded, setCacheLoaded] = useState(false);

  useEffect(() => { const t = localStorage.getItem('util_token'), u = localStorage.getItem('util_user'); if (t && u) { setToken(t); const p = JSON.parse(u); setUser(p); if (p.role !== 'admin' && p.assigned_warehouse) setSelectedWarehouse(p.assigned_warehouse); } }, []);
// 🚀 LOAD FROM CACHE ON MOUNT - Instant load!
  // ⚠️ DO NOT load weeklyEntries from cache - always fetch fresh to avoid stale data
  useEffect(() => {
    const cached = getCache();
    if (cached?.rawWarehouseData?.length > 0) {
      console.log('⚡ Loading from cache - instant!');
      setRawWarehouseData(cached.rawWarehouseData || []);
      setRawVirtualData(cached.rawVirtualData || []);
      setCustomerStorageData(cached.customerStorageData || []);
      setWarehouseMappings(cached.warehouseMappings || []);
      setTopClients(cached.topClients || []);
      setClientsList(cached.clientsList || []);
      if (cached.dataSource) setDataSource(cached.dataSource);
      if (cached.snapshotDate) setSnapshotDate(cached.snapshotDate);
      setCacheLoaded(true);
      // ⚠️ DO NOT set initialLoadDone here - let fetchData still run to get fresh manual entries
    }
  }, []);

  // Weekly entries are now fetched in fetchData - removed duplicate useEffect
  const handleLogin = (u: User, t: string) => { setUser(u); setToken(t); localStorage.setItem('util_token', t); localStorage.setItem('util_user', JSON.stringify(u)); if (u.role !== 'admin' && u.assigned_warehouse) setSelectedWarehouse(u.assigned_warehouse); };
 const handleLogout = () => { setUser(null); setToken(null); localStorage.removeItem('util_token'); localStorage.removeItem('util_user'); clearCache(); };

  // 🔥 SEPARATE function for manual entries - no cacheLoaded dependency!
  const fetchManualEntries = useCallback(async () => {
    if (!token) return;
    console.log('📥 Fetching weekly entries for year:', selectedYear, 'and previous year:', selectedYear - 1);

    let retries = 3;
    let success = false;

    while (retries > 0 && !success) {
      try {
        // Fetch BOTH current year AND previous year entries so carry-forward works across year boundaries
        const [currentRes, prevRes] = await Promise.all([
          fetch(`${AI_API_BASE}/weekly-entries?year=${selectedYear}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${AI_API_BASE}/weekly-entries?year=${selectedYear - 1}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).catch(() => null)
        ]);

        let allEntries: ManualWeeklyEntry[] = [];

        if (currentRes.ok) {
          const d = await currentRes.json();
          if (d.success && d.data) {
            allEntries = [...allEntries, ...d.data];
          }
        }

        if (prevRes?.ok) {
          const d = await prevRes.json();
          if (d.success && d.data) {
            allEntries = [...allEntries, ...d.data];
            console.log('✅ Also loaded', d.data.length, 'entries from previous year', selectedYear - 1);
          }
        }

        console.log('✅ Loaded total', allEntries.length, 'weekly entries (current + previous year)');
        setWeeklyEntries(allEntries);
        success = true;

        if (!currentRes.ok) {
          console.error('❌ Weekly entries API failed:', currentRes.status, 'retries left:', retries - 1);
          retries--;
          if (retries > 0) await new Promise(r => setTimeout(r, 1000));
        }
      } catch (e: any) {
        console.error('❌ Failed to fetch weekly entries:', e, 'retries left:', retries - 1);
        retries--;
        if (retries > 0) await new Promise(r => setTimeout(r, 1000));
      }
    }
  }, [token, selectedYear]);

  // WAREHOUSE DATA FETCH - ALWAYS fetch fresh data
  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!token) return;

    // Fetch manual entries
    await fetchManualEntries();

    // ALWAYS fetch warehouse data from API
    setLoading(true);
    console.log('🔄 Fetching warehouse data from API...');

    try {
      const [mapRes, whRes, virtRes, custRes] = await Promise.all([
        fetch(`${API_BASE}/warehouse-mapping`).catch(() => null),
        fetch(`${API_BASE}/warehouse`),
        fetch(`${API_BASE}/warehouse/virtual`).catch(() => null),
        fetch(`${API_BASE}/warehouse/customer-storage-locations`).catch(() => null),
      ]);

      let newMappings: any[] = [], newWhData: any[] = [], newVirtData: any[] = [], newCustData: any[] = [];

      if (mapRes?.ok) { const d = await mapRes.json(); if (d.success) newMappings = d.data || []; }
      if (whRes?.ok) { const d = await whRes.json(); if (d.success) newWhData = d.data || []; }
      if (virtRes?.ok) { const d = await virtRes.json(); if (d.success) newVirtData = d.data || []; }
      if (custRes?.ok) { const d = await custRes.json(); if (d.success) newCustData = d.data || []; }

      setWarehouseMappings(newMappings);
      setRawWarehouseData(newWhData);
      setRawVirtualData(newVirtData);
      setCustomerStorageData(newCustData);

      setCache({
        warehouseMappings: newMappings,
        rawWarehouseData: newWhData,
        rawVirtualData: newVirtData,
        customerStorageData: newCustData,
      });
      setCacheLoaded(true);

    } catch (e) { console.error('Error:', e); }
    setLoading(false);
  }, [token, fetchManualEntries]);
// 🔄 REFRESH - Clears cache and fetches fresh data
  const handleRefresh = useCallback(() => {
    console.log('🔄 User clicked Refresh');
    clearCache();
    setCacheLoaded(false);
    fetchData(true);
  }, [fetchData]);
  const weeklyEntriesRef = React.useRef(weeklyEntries);
  useEffect(() => { weeklyEntriesRef.current = weeklyEntries; }, [weeklyEntries]);

  const fetchTopClients = useCallback(async (whFilter: string | null, week: number, year: number, entriesOverride?: ManualWeeklyEntry[]) => {
    const entries = entriesOverride || weeklyEntriesRef.current;
    console.log('📊 fetchTopClients - manual entries count:', entries.length);
    try {
      const clientMap = new Map<string, ClientData>();
      let hasSnap = false, snapInfo: any = null;
      try { const r = await fetch(`${API_BASE}/snapshots/check/${week}/${year}`); const d = await r.json(); hasSnap = d.exists === true; snapInfo = d.data; } catch { }
      // Calculate previous week
      const prevWeek = week > 1 ? week - 1 : 52;
      const prevYear = week > 1 ? year : year - 1;

      // ALWAYS calculate previous week data first (for both snapshot and realtime)
      const prevTotals: { [k: string]: number } = {};
      let hasPrevSnap = false;
      try {
        const pr = await fetch(`${API_BASE}/snapshots/check/${prevWeek}/${prevYear}`);
        const pd = await pr.json();
        if (pd.exists === true) {
          hasPrevSnap = true;
          let prevUrl = `${API_BASE}/snapshots/clients?week=${prevWeek}&year=${prevYear}&limit=500`;
          if (whFilter) prevUrl += `&warehouse=${encodeURIComponent(whFilter)}`;
          const psr = await fetch(prevUrl);
          const psd = await psr.json();
          if (psd.success && psd.data?.length > 0) {
            psd.data.forEach((c: any) => { prevTotals[c.CLIENT] = (prevTotals[c.CLIENT] || 0) + (c.CURRENT_PALLETS || 0); });
          }
          console.log('📊 fetchTopClients - previous week from SNAPSHOT:', Object.keys(prevTotals).length, 'clients');
        }
      } catch { }

      // If no previous snapshot, use manual entries from ALL previous weeks (find most recent)
      if (!hasPrevSnap) {
        // Get all entries before current week, sorted by week DESC (most recent first)
        const allPrevious = entries
          .filter(e => {
            const isBeforeCurrent = e.year < year || (e.year === year && e.week_number < week);
            const matchesWarehouse = !whFilter || e.warehouse === whFilter;
            return isBeforeCurrent && matchesWarehouse;
          })
          .sort((a, b) => {
            if (a.year !== b.year) return b.year - a.year;
            return b.week_number - a.week_number;
          });

        // Aggregate all entries by client + week, then pick the most recent week's total
        const clientWeekData: { [client: string]: { [week: number]: number } } = {};

        allPrevious.forEach(e => {
          if (e.closing_balance > 0) {
            if (!clientWeekData[e.client_name]) {
              clientWeekData[e.client_name] = {};
            }
            // Use year*100 + week as key to handle year boundaries correctly
            const weekKey = e.year * 100 + e.week_number;
            if (!clientWeekData[e.client_name][weekKey]) {
              clientWeekData[e.client_name][weekKey] = 0;
            }
            // SUM all entries for same client + same week
            clientWeekData[e.client_name][weekKey] += e.closing_balance || 0;
          }
        });

        // For each client, get the total from the most recent week
        Object.entries(clientWeekData).forEach(([client, weekData]) => {
          const weeks = Object.keys(weekData).map(Number).sort((a, b) => b - a);
          if (weeks.length > 0) {
            const latestWeek = weeks[0];
            prevTotals[client] = weekData[latestWeek];
          }
        });

        console.log('📊 fetchTopClients - previous week from MANUAL entries:', prevTotals);
      }

      // Now handle current week - check for snapshot
      if (hasSnap) {
        let url = `${API_BASE}/snapshots/clients?week=${week}&year=${year}&limit=500`;
        if (whFilter) url += `&warehouse=${encodeURIComponent(whFilter)}`;
        const r = await fetch(url); const d = await r.json();
        if (d.success && d.data?.length > 0) {
          // Use snapshot for CURRENT_PALLETS but calculate PREVIOUS_PALLETS from prevTotals
          const clients = d.data.map((c: any) => ({
            CLIENT: c.CLIENT,
            CURRENT_PALLETS: c.CURRENT_PALLETS || 0,
            PREVIOUS_PALLETS: prevTotals[c.CLIENT] || c.PREVIOUS_PALLETS || 0,
            SOURCE: 'SNAPSHOT' as const
          }));

          // Also add clients that only exist in previous week (not in current snapshot)
          Object.entries(prevTotals).forEach(([client, pallets]) => {
            if (!clients.find((c: ClientData) => c.CLIENT === client) && pallets > 0) {
              console.log(`📊 Adding previous-only client to snapshot: ${client} with ${pallets} pallets`);
              clients.push({ CLIENT: client, CURRENT_PALLETS: pallets, PREVIOUS_PALLETS: pallets, SOURCE: 'SNAPSHOT' as const });
            }
          });

          // CARRY FORWARD ALL manual clients from any week
          const allManualSnap: { [k: string]: number } = {};
          entries
            .filter(e => {
              const isBefore = e.year < year || (e.year === year && e.week_number <= week);
              const matchesWh = !whFilter || e.warehouse === whFilter;
              return isBefore && matchesWh && (e.closing_balance || 0) > 0;
            })
            .sort((a, b) => b.year - a.year || b.week_number - a.week_number)
            .forEach(e => {
              if (!allManualSnap[e.client_name]) allManualSnap[e.client_name] = e.closing_balance || 0;
            });
          Object.entries(allManualSnap).forEach(([client, pallets]) => {
            if (!clients.find((c: ClientData) => c.CLIENT === client) && pallets > 0) {
              clients.push({ CLIENT: client, CURRENT_PALLETS: pallets, PREVIOUS_PALLETS: pallets, SOURCE: 'MANUAL' as const });
            }
          });

          clients.sort((a: ClientData, b: ClientData) => b.CURRENT_PALLETS - a.CURRENT_PALLETS);
          setTopClients(clients); setClientsList(clients.map((c: ClientData) => c.CLIENT)); setDataSource('SNAPSHOT'); setSnapshotDate(snapInfo?.snapshot_date || null);
          setCache({ topClients: clients, clientsList: clients.map((c: ClientData) => c.CLIENT), dataSource: 'SNAPSHOT', snapshotDate: snapInfo?.snapshot_date }); return;
        }
      }
      setDataSource('REALTIME'); setSnapshotDate(null);

      // Get current week IBM data
      let apiUrl = `${API_BASE}/utilization/top-clients-comparison?limit=500`;
      if (whFilter) apiUrl += `&warehouse=${whFilter}`;
      const r = await fetch(apiUrl); const d = await r.json();
      if (d.success && d.data) d.data.forEach((c: any) => {
        const prevPallets = prevTotals[c.CLIENT] || 0;
        clientMap.set(c.CLIENT, { CLIENT: c.CLIENT, CURRENT_PALLETS: c.CURRENT_PALLETS || 0, PREVIOUS_PALLETS: prevPallets, SOURCE: 'IBM' as const });
      });

      // Get current week manual entries
      const currentManual = entries.filter(e => e.week_number === week && e.year === year && (!whFilter || e.warehouse === whFilter));
      const currTotals: { [k: string]: number } = {};
      currentManual.forEach(e => { currTotals[e.client_name] = (currTotals[e.client_name] || 0) + (e.closing_balance || 0); });

      // Add current week manual entries
      currentManual.forEach(entry => {
        const prevPallets = prevTotals[entry.client_name] || 0;
        if (!clientMap.has(entry.client_name)) {
          clientMap.set(entry.client_name, { CLIENT: entry.client_name, CURRENT_PALLETS: entry.closing_balance || 0, PREVIOUS_PALLETS: prevPallets, SOURCE: 'MANUAL' as const });
        } else {
          const ex = clientMap.get(entry.client_name)!;
          ex.SOURCE = 'MERGED';
          ex.CURRENT_PALLETS += entry.closing_balance || 0;
        }
      });

      // CARRY FORWARD: Add clients that only exist in previous week (not in current)
      // Use their previous closing_balance as current (they still have stock)
      Object.entries(prevTotals).forEach(([client, pallets]) => {
        if (!clientMap.has(client) && pallets > 0) {
          console.log(`📊 Carrying forward client ${client}: ${pallets} pallets from previous week`);
          clientMap.set(client, { CLIENT: client, CURRENT_PALLETS: pallets, PREVIOUS_PALLETS: pallets, SOURCE: 'MANUAL' as const });
        }
      });

      // CARRY FORWARD FROM ALL MANUAL ENTRIES: Find clients from any previous week that are not yet in clientMap
      // This ensures manual-only clients always appear even if they had no inbound/outbound recently
      const allManualClients: { [k: string]: { pallets: number; week: number; year: number } } = {};
      entries
        .filter(e => {
          const isBefore = e.year < year || (e.year === year && e.week_number <= week);
          const matchesWh = !whFilter || e.warehouse === whFilter;
          return isBefore && matchesWh && (e.closing_balance || 0) > 0;
        })
        .sort((a, b) => b.year - a.year || b.week_number - a.week_number)
        .forEach(e => {
          if (!allManualClients[e.client_name]) {
            allManualClients[e.client_name] = { pallets: e.closing_balance || 0, week: e.week_number, year: e.year };
          } else {
            // If same most-recent week, sum up
            const existing = allManualClients[e.client_name];
            if (existing.year === e.year && existing.week === e.week_number) {
              existing.pallets += e.closing_balance || 0;
            }
          }
        });

      Object.entries(allManualClients).forEach(([client, data]) => {
        if (!clientMap.has(client) && data.pallets > 0) {
          console.log(`📊 Carrying forward manual client ${client}: ${data.pallets} pallets from W${data.week}/${data.year}`);
          clientMap.set(client, { CLIENT: client, CURRENT_PALLETS: data.pallets, PREVIOUS_PALLETS: data.pallets, SOURCE: 'MANUAL' as const });
        }
      });

      const all = Array.from(clientMap.values()).sort((a, b) => b.CURRENT_PALLETS - a.CURRENT_PALLETS);
      setTopClients(all); setClientsList(all.map(c => c.CLIENT));
      setCache({ topClients: all, clientsList: all.map(c => c.CLIENT), dataSource: 'REALTIME', snapshotDate: null });
    } catch { }
  }, []);

  const calculateStats = useCallback((whFilter: string | null, entriesOverride?: ManualWeeklyEntry[]) => {
    const entries = entriesOverride || weeklyEntries;
    console.log('📊 calculateStats CALLED');
    console.log('📊 rawWarehouseData count:', rawWarehouseData.length);
    console.log('📊 rawWarehouseData sample:', rawWarehouseData.slice(0, 3).map(d => ({ wh: d.WAREHOUSE, area: d.STORAGE_AREA, segments: d.TOTAL_NO_OF_SEGMENTS, free: d.FREE_SEGMENTS })));
    console.log('📊 - entries count:', entries.length, '(override:', !!entriesOverride, ')');
    console.log('📊 - selectedWeek:', selectedWeek, 'selectedYear:', selectedYear);
    console.log('📊 - entries sample:', entries.slice(0, 3).map(e => ({ wh: e.warehouse, week: e.week_number, year: e.year, closing: e.closing_balance, cap: e.area_capacity })));
    const whGroups: { [k: string]: WarehouseData } = {};

    // ========== TEMP_BLOCKED CALCULATION ==========
    // Identify TEMP_BLOCKED bins (LPFSTA = '2' OR OCCUPANCY_STATUS = 'TEMP_BLOCKED')
    const tempBlockedData = rawWarehouseData.filter(item =>
      String(item.LPFSTA || '').trim() === '2' ||
      item.OCCUPANCY_STATUS === 'TEMP_BLOCKED'
    );
    const tempBlockedBins = tempBlockedData.length;
    // For TEMP_BLOCKED: count FULL capacity as blocked (use pallet capacity rule)
    const tempBlockedPallets = tempBlockedData.reduce((sum, item) =>
      sum + calcPalletCapacity(item.TOTAL_NO_OF_SEGMENTS || 0, item.NUMBER_OF_LEVELS || 0), 0);

    // Track temp blocked by warehouse
    const tempBlockedByWarehouse: { [wh: string]: number } = {};
    tempBlockedData.forEach(item => {
      const lw = getLogicalWarehouseStatic(item.WAREHOUSE, item.STORAGE_AREA);
      tempBlockedByWarehouse[lw] = (tempBlockedByWarehouse[lw] || 0) + calcPalletCapacity(item.TOTAL_NO_OF_SEGMENTS || 0, item.NUMBER_OF_LEVELS || 0);
    });

    console.log('📊 TEMP_BLOCKED: bins=', tempBlockedBins, 'pallets=', tempBlockedPallets, 'by warehouse:', tempBlockedByWarehouse);

    // Process NORMAL bins (exclude TEMP_BLOCKED to avoid double-counting)
    const normalBinData = rawWarehouseData.filter(item =>
      String(item.LPFSTA || '').trim() !== '2' &&
      item.OCCUPANCY_STATUS !== 'TEMP_BLOCKED'
    );

    // Track normal pallets by warehouse (using pallet capacity rules)
    const normalPalletsByWarehouse: { [wh: string]: number } = {};
    normalBinData.forEach(item => {
      const lw = getLogicalWarehouseStatic(item.WAREHOUSE, item.STORAGE_AREA);
      if (!whGroups[lw]) whGroups[lw] = { WAREHOUSE: lw, CAPACITY: 0, PALLET_COUNT: 0, FREE_COUNT: 0, UTILIZATION_PCT: 0, TEMP_BLOCKED: 0, NORMAL_PALLETS: 0, SOURCE: 'IBM' };
      const palletCap = calcPalletCapacity(item.TOTAL_NO_OF_SEGMENTS || 0, item.NUMBER_OF_LEVELS || 0);
      const usedPallets = calcUsedPallets(item.TOTAL_NO_OF_SEGMENTS || 0, item.FREE_SEGMENTS || 0, item.NUMBER_OF_LEVELS || 0);
      whGroups[lw].CAPACITY += palletCap;
      whGroups[lw].PALLET_COUNT += usedPallets;
      normalPalletsByWarehouse[lw] = (normalPalletsByWarehouse[lw] || 0) + usedPallets;
    });

    // Add TEMP_BLOCKED bins to capacity and pallet count (blocked bins = full capacity used)
    tempBlockedData.forEach(item => {
      const lw = getLogicalWarehouseStatic(item.WAREHOUSE, item.STORAGE_AREA);
      if (!whGroups[lw]) whGroups[lw] = { WAREHOUSE: lw, CAPACITY: 0, PALLET_COUNT: 0, FREE_COUNT: 0, UTILIZATION_PCT: 0, TEMP_BLOCKED: 0, NORMAL_PALLETS: 0, SOURCE: 'IBM' };
      const palletCap = calcPalletCapacity(item.TOTAL_NO_OF_SEGMENTS || 0, item.NUMBER_OF_LEVELS || 0);
      whGroups[lw].CAPACITY += palletCap;
      whGroups[lw].PALLET_COUNT += palletCap; // Temp blocked = 100% used
    });

    // Set TEMP_BLOCKED and NORMAL_PALLETS for each warehouse
    Object.keys(whGroups).forEach(wh => {
      whGroups[wh].TEMP_BLOCKED = tempBlockedByWarehouse[wh] || 0;
      whGroups[wh].NORMAL_PALLETS = normalPalletsByWarehouse[wh] || 0;
    });

    console.log('📊 IBM warehouses after processing:', Object.keys(whGroups).length, Object.keys(whGroups));
    console.log('📊 IBM warehouse sample:', Object.values(whGroups).slice(0, 3).map(w => ({ wh: w.WAREHOUSE, cap: w.CAPACITY, pal: w.PALLET_COUNT, normal: w.NORMAL_PALLETS, temp: w.TEMP_BLOCKED })));

    rawVirtualData.forEach(item => { const lw = getLogicalWarehouseStatic(item.WAREHOUSE, item.STORAGE_AREA); if (whGroups[lw]) { const vUsed = calcUsedPallets(item.TOTAL_NO_OF_SEGMENTS || 0, item.FREE_SEGMENTS || 0, item.NUMBER_OF_LEVELS || 0); whGroups[lw].PALLET_COUNT += vUsed; whGroups[lw].NORMAL_PALLETS = (whGroups[lw].NORMAL_PALLETS || 0) + vUsed; } });
    customerStorageData.forEach(item => { if (item.STORAGE_TYPE !== 'FLOOR' && item.STORAGE_TYPE !== 'BULK') return; const lw = getLogicalWarehouseStatic(item.WAREHOUSE, item.STORAGE_LOCATION); if (whGroups[lw]) { whGroups[lw].PALLET_COUNT += item.USED_PALLETS || 0; whGroups[lw].NORMAL_PALLETS = (whGroups[lw].NORMAL_PALLETS || 0) + (item.USED_PALLETS || 0); } });

    // Get current week entries for PALLETS
    const currentWeekEntries = entries.filter(e => e.week_number === selectedWeek && e.year === selectedYear);
    console.log('📊 calculateStats - total entries:', entries.length);
    console.log('📊 calculateStats - currentWeekEntries:', currentWeekEntries.length, 'for week', selectedWeek, 'year', selectedYear);
    console.log('📊 calculateStats - entries by week:', entries.reduce((acc, e) => { const k = `W${e.week_number}`; acc[k] = (acc[k] || 0) + 1; return acc; }, {} as Record<string, number>));

    // Get CAPACITY from latest entry per warehouse/area (capacity persists across weeks)
    const latestCapacity: { [k: string]: { area: string; capacity: number } } = {};
    // Sort entries by year DESC, week DESC to get latest first
    const sortedEntries = [...entries].sort((a, b) => b.year - a.year || b.week_number - a.week_number);
    sortedEntries.forEach(e => {
      if (e.area_capacity && e.area_capacity > 0) {
        const key = `${e.warehouse}__${e.warehouse_area || 'default'}`;
        if (!latestCapacity[key]) {
          latestCapacity[key] = { area: e.warehouse_area || 'default', capacity: e.area_capacity };
        }
      }
    });

    // Calculate manual totals - PALLETS from current week, CAPACITY from latest entries
    const manTotals: { [k: string]: { p: number; c: number; areas: Set<string> } } = {};

    // First add capacity from all known areas (even if no current week entries)
    Object.entries(latestCapacity).forEach(([key, data]) => {
      const wh = key.split('__')[0];
      if (!manTotals[wh]) manTotals[wh] = { p: 0, c: 0, areas: new Set() };
      if (!manTotals[wh].areas.has(data.area)) {
        manTotals[wh].areas.add(data.area);
        manTotals[wh].c += data.capacity;
      }
    });

    // Then add pallets from current week entries
    currentWeekEntries.forEach(e => {
      if (!manTotals[e.warehouse]) manTotals[e.warehouse] = { p: 0, c: 0, areas: new Set() };
      manTotals[e.warehouse].p += e.closing_balance || 0;
    });

    // CARRY FORWARD: For warehouses with no current week entries,
    // use the most recent data from previous weeks - SUM ALL ENTRIES per warehouse per week
    // Use year*100+week as key to handle cross-year boundaries correctly
    const previousWeekData: { [wh: string]: { [yearWeek: number]: { pallets: number; capacity: number } } } = {};

    // Aggregate all entries by warehouse and year+week (up to and including current week)
    sortedEntries.forEach(e => {
      if (e.year < selectedYear || (e.year === selectedYear && e.week_number <= selectedWeek)) {
        if (!previousWeekData[e.warehouse]) {
          previousWeekData[e.warehouse] = {};
        }
        const yearWeekKey = e.year * 100 + e.week_number;
        if (!previousWeekData[e.warehouse][yearWeekKey]) {
          previousWeekData[e.warehouse][yearWeekKey] = { pallets: 0, capacity: 0 };
        }
        // SUM all entries for same warehouse + week
        previousWeekData[e.warehouse][yearWeekKey].pallets += e.closing_balance || 0;
        previousWeekData[e.warehouse][yearWeekKey].capacity += e.capacity || e.area_capacity || 0;
      }
    });

    // Now get the MOST RECENT week's TOTAL for each warehouse
    const latestPreviousData: { [key: string]: { pallets: number; capacity: number; week: number } } = {};
    Object.entries(previousWeekData).forEach(([wh, weekData]) => {
      // Find the most recent year+week (higher number = more recent)
      const yearWeeks = Object.keys(weekData).map(Number).sort((a, b) => b - a);
      if (yearWeeks.length > 0) {
        const latestYearWeek = yearWeeks[0];
        latestPreviousData[wh] = {
          pallets: weekData[latestYearWeek].pallets,
          capacity: weekData[latestYearWeek].capacity,
          week: latestYearWeek % 100
        };
      }
    });

    console.log('📊 Previous week data for carry-forward:', latestPreviousData);
    console.log('📊 Current week:', selectedWeek, 'Year:', selectedYear);
    console.log('📊 All warehouses in entries:', Array.from(new Set(entries.map(e => e.warehouse))));

    // DEBUG: Show all entries for RUH_M
    const ruhMEntries = entries.filter(e => e.warehouse === 'RUH_M' || e.warehouse?.includes('RUH'));
    console.log('📊 DEBUG RUH entries in weeklyEntries:', ruhMEntries.map(e => ({
      week: e.week_number,
      year: e.year,
      warehouse: e.warehouse,
      area: e.warehouse_area,
      client: e.client_name,
      closing: e.closing_balance,
      capacity: e.capacity,
      area_capacity: e.area_capacity
    })));

    // DEBUG: Check if RUH_M should be carried forward
    if (latestPreviousData['RUH_M']) {
      console.log('📊 RUH_M previous data found:', latestPreviousData['RUH_M']);
    } else {
      console.log('📊 RUH_M NOT in latestPreviousData');
    }

    // DEBUG: Show manTotals for RUH_M
    console.log('📊 manTotals before carry-forward:', JSON.stringify(manTotals, (k, v) => v instanceof Set ? Array.from(v) : v));

    // Add carried-forward data for warehouses that have NO current week entries
    // First, check which warehouses in latestPreviousData are NOT in current week
    const currentWeekWarehouses = new Set(currentWeekEntries.map(e => e.warehouse));

    Object.entries(latestPreviousData).forEach(([wh, prevData]) => {
      // If this warehouse has NO current week entry, carry forward everything
      if (!currentWeekWarehouses.has(wh) && (prevData.pallets > 0 || prevData.capacity > 0)) {
        console.log(`📊 Carrying forward for ${wh}: ${prevData.pallets} pallets, ${prevData.capacity} capacity from week ${prevData.week}`);

        if (!manTotals[wh]) {
          manTotals[wh] = { p: 0, c: 0, areas: new Set() };
        }

        // Carry forward pallets if current is 0
        if (manTotals[wh].p === 0 && prevData.pallets > 0) {
          manTotals[wh].p = prevData.pallets;
        }

        // Carry forward capacity if current is 0
        if (manTotals[wh].c === 0 && prevData.capacity > 0) {
          manTotals[wh].c = prevData.capacity;
        }
      }
    });

    // Also check for warehouses in manTotals that have capacity but 0 pallets
    Object.entries(manTotals).forEach(([wh, data]) => {
      if (data.p === 0 && latestPreviousData[wh]?.pallets > 0) {
        console.log(`📊 Filling missing pallets for ${wh}: ${latestPreviousData[wh].pallets} from week ${latestPreviousData[wh].week}`);
        data.p = latestPreviousData[wh].pallets;
      }
    });

    // DEBUG: Final manTotals after carry-forward
    console.log('📊 manTotals AFTER carry-forward:', JSON.stringify(manTotals, (k, v) => v instanceof Set ? Array.from(v) : v));
    if (manTotals['RUH_M']) {
      console.log('📊 RUH_M final in manTotals: pallets=', manTotals['RUH_M'].p, 'capacity=', manTotals['RUH_M'].c);
    }

    console.log('📊 calculateStats - manual warehouses to merge:', Object.keys(manTotals));
    Object.entries(manTotals).forEach(([wh, d]) => {
      if (!whGroups[wh]) {
        // New MANUAL warehouse - set NORMAL_PALLETS = PALLET_COUNT (no temp blocked for manual)
        whGroups[wh] = { WAREHOUSE: wh, CAPACITY: d.c, PALLET_COUNT: d.p, FREE_COUNT: 0, UTILIZATION_PCT: 0, TEMP_BLOCKED: 0, NORMAL_PALLETS: d.p, SOURCE: 'MANUAL' };
      } else {
        whGroups[wh].SOURCE = 'MERGED';
        whGroups[wh].PALLET_COUNT += d.p;
        whGroups[wh].CAPACITY += d.c;
        // Add manual pallets to NORMAL_PALLETS (manual entries don't have temp blocked)
        whGroups[wh].NORMAL_PALLETS = (whGroups[wh].NORMAL_PALLETS || 0) + d.p;
      }
    });
    const all = Object.values(whGroups).map((wh: WarehouseData) => { wh.FREE_COUNT = Math.max(0, (wh.CAPACITY || 0) - (wh.PALLET_COUNT || 0)); wh.UTILIZATION_PCT = (wh.CAPACITY || 0) > 0 ? Math.round(((wh.PALLET_COUNT || 0) / wh.CAPACITY) * 100 * 100) / 100 : 0; return wh; }).sort((a, b) => a.WAREHOUSE.localeCompare(b.WAREHOUSE));
    console.log('📊 calculateStats - final warehouses:', all.map(w => `${w.WAREHOUSE}(${w.SOURCE})`).join(', '));
    const totCap = all.reduce((s, w) => s + (w.CAPACITY || 0), 0), totPal = all.reduce((s, w) => s + (w.PALLET_COUNT || 0), 0);
    let dispWh = all; if (whFilter) dispWh = all.filter(w => w.WAREHOUSE === whFilter);
    setWarehouses(whFilter ? dispWh : all);
    if (!whFilter) setOverview({ CAPACITY: totCap, TOTAL_PALLETS: totPal, FREE_PALLETS: Math.max(0, totCap - totPal), UTILIZATION_PCT: totCap > 0 ? Math.round((totPal / totCap) * 100 * 100) / 100 : 0, TEMP_BLOCKED_PALLETS: tempBlockedPallets, TEMP_BLOCKED_BINS: tempBlockedBins });
    else { const fCap = dispWh.reduce((s, w) => s + (w.CAPACITY || 0), 0), fPal = dispWh.reduce((s, w) => s + (w.PALLET_COUNT || 0), 0); setOverview({ CAPACITY: fCap, TOTAL_PALLETS: fPal, FREE_PALLETS: Math.max(0, fCap - fPal), UTILIZATION_PCT: fCap > 0 ? Math.round((fPal / fCap) * 100 * 100) / 100 : 0, TEMP_BLOCKED_PALLETS: tempBlockedPallets, TEMP_BLOCKED_BINS: tempBlockedBins }); }
  }, [rawWarehouseData, rawVirtualData, customerStorageData, weeklyEntries, selectedWeek, selectedYear]);

  // 🔥 CRITICAL: Always call fetchData when token exists - weekly entries must be fetched fresh every time!
  useEffect(() => {
    if (token && !initialLoadDone) {
      setInitialLoadDone(true);
      fetchData();
    }
  }, [token, initialLoadDone, fetchData]);

  // 🔥 Re-fetch weekly entries when year changes
  const [prevYear, setPrevYear] = useState(selectedYear);
  useEffect(() => {
    if (token && selectedYear !== prevYear) {
      console.log('📅 Year changed from', prevYear, 'to', selectedYear, '- re-fetching entries');
      setPrevYear(selectedYear);
      fetchManualEntries(); // Only fetch manual entries, not all data
    }
  }, [token, selectedYear, prevYear, fetchManualEntries]);

  useEffect(() => {
    console.log('📊 Triggering calculateStats with', weeklyEntries.length, 'manual entries');
    calculateStats(selectedWarehouse, weeklyEntries);
  }, [rawWarehouseData, rawVirtualData, customerStorageData, selectedWarehouse, weeklyEntries, selectedWeek, selectedYear, warehouseMappings, calculateStats]);
  // Fetch top clients when warehouse/week/year changes OR when entries are loaded
  useEffect(() => {
    if (token && weeklyEntries) {
      console.log('📊 Triggering fetchTopClients with', weeklyEntries.length, 'manual entries');
      fetchTopClients(selectedWarehouse, selectedWeek, selectedYear, weeklyEntries);
    }
  }, [selectedWarehouse, token, selectedWeek, selectedYear, weeklyEntries, fetchTopClients]);

  // 📊 AUTO-SAVE weekly utilization summary to database
  useEffect(() => {
    const autoSaveUtilization = async () => {
      if (!token || !overview || overview.CAPACITY === 0) return;
      try {
        await fetch(`${AI_API_BASE}/weekly-utilization-summary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            year: selectedYear,
            week_number: selectedWeek,
            warehouse: selectedWarehouse || 'ALL',
            total_pallets: overview.TOTAL_PALLETS,
            total_capacity: overview.CAPACITY,
            utilization_pct: overview.UTILIZATION_PCT,
            free_pallets: overview.FREE_PALLETS
          })
        });
        console.log(`📊 Auto-saved utilization: Week ${selectedWeek}/${selectedYear} - ${selectedWarehouse || 'ALL'}: ${overview.UTILIZATION_PCT}%`);
      } catch (e) {
        console.error('Auto-save utilization failed:', e);
      }
    };
    // Debounce: save after 2 seconds to avoid too many requests
    const timer = setTimeout(autoSaveUtilization, 2000);
    return () => clearTimeout(timer);
  }, [overview, selectedWeek, selectedYear, selectedWarehouse, token]);

  const handleWarehouseSelect = (wh: string | null) => { if (user?.role === 'admin') { setSelectedWarehouse(wh); setSelectedArea(null); } };
  const handleDeleteEntry = async (id: number) => { if (!window.confirm('Delete?')) return; try { const r = await fetch(`${AI_API_BASE}/weekly-entries/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }); const d = await r.json(); if (d.success) fetchManualEntries(); } catch { } };

  const weeklyTrendData = useMemo(() => {
    const filtered = weeklyEntries.filter(e => (!selectedWarehouse || e.warehouse === selectedWarehouse) && e.year === selectedYear);
    const byWeek: { [k: number]: { inbound: number; outbound: number; closing: number } } = {};
    filtered.forEach(e => { if (!byWeek[e.week_number]) byWeek[e.week_number] = { inbound: 0, outbound: 0, closing: 0 }; byWeek[e.week_number].inbound += e.inbound || 0; byWeek[e.week_number].outbound += e.outbound || 0; byWeek[e.week_number].closing += e.closing_balance || 0; });
    return Object.entries(byWeek).map(([w, d]) => ({ week: `W${w}`, ...d })).sort((a, b) => parseInt(a.week.slice(1)) - parseInt(b.week.slice(1)));
  }, [weeklyEntries, selectedWarehouse, selectedYear]);

  if (!user || !token) return <LoginPage onLogin={handleLogin} />;
  const currentWeek = new Date().getWeek();
  const utilColor = overview?.UTILIZATION_PCT ? overview.UTILIZATION_PCT > 100 ? colors.danger : overview.UTILIZATION_PCT > 85 ? colors.accent : colors.primary : colors.textMuted;
  const getBarColor = (src?: string) => src === 'MANUAL' ? colors.secondary : src === 'SNAPSHOT' ? '#10b981' : src === 'MERGED' ? colors.success : colors.primary;

  return (
    <div style={styles.container}>
      {/* SIDEBAR */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarLogo}><div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'linear-gradient(135deg, #118DFF 0%, #0066CC 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 700, color: 'white' }}>H</div><div><div style={{ fontSize: '16px', fontWeight: 700 }}>HALA</div><div style={{ fontSize: '9px', opacity: 0.6 }}>Supply Chain</div></div></div></div>
        <div style={styles.sidebarNav}>
          <div style={activeTab === 'utilization' ? styles.navItemActive : styles.navItem} onClick={() => setActiveTab('utilization')}><span>📊</span> Utilization</div>
          <div style={activeTab === 'bins' ? styles.navItemActive : styles.navItem} onClick={() => setActiveTab('bins')}><span>📦</span> Bin Analysis</div>
          <div style={activeTab === 'weekly' ? styles.navItemActive : styles.navItem} onClick={() => setActiveTab('weekly')}><span>📅</span> Weekly</div>
          <div style={activeTab === 'trends' ? styles.navItemActive : styles.navItem} onClick={() => setActiveTab('trends')}><span>📈</span> Trends</div>
          <div style={activeTab === '3d' ? styles.navItemActive : styles.navItem} onClick={() => setActiveTab('3d')}><span>🏭</span> 3D View</div>
          {user.role === 'admin' && <div style={activeTab === 'users' ? styles.navItemActive : styles.navItem} onClick={() => setActiveTab('users')}><span>👥</span> Users</div>}
          {user.role === 'admin' && <div style={styles.navItem} onClick={() => setShowTeamsSettings(true)}><span>📱</span> Teams</div>}
        </div>
        <div style={styles.weekNumbers}><div style={{ fontSize: '11px', marginBottom: '10px', opacity: 0.7, fontWeight: 600 }}>Week</div><div style={styles.weekGrid}>{Array.from({ length: currentWeek }, (_, i) => i + 1).map(w => <button key={w} style={w === selectedWeek ? styles.weekBtnActive : styles.weekBtn} onClick={() => setSelectedWeek(w)}>{w}</button>)}</div></div>
        <div style={styles.userInfo}>
          <ThemeToggle style={{ marginBottom: '12px', width: '100%' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-accent) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 600, color: 'white', flexShrink: 0 }}>
              {user.display_name.charAt(0).toUpperCase()}
            </div>
            <div style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.display_name}</div>
              <div style={{ fontSize: '10px', opacity: 0.6, color: 'var(--theme-text-secondary)' }}>{user.role === 'admin' ? 'Admin' : user.assigned_warehouse}</div>
            </div>
          </div>
          <button style={{ width: '100%', padding: '8px', backgroundColor: 'var(--theme-background-secondary)', border: '1px solid var(--theme-border-light)', borderRadius: '5px', color: 'var(--theme-text)', fontSize: '12px', cursor: 'pointer', transition: 'all 0.2s' }} onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {/* MAIN */}
      <div style={styles.mainContent}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', paddingBottom: '14px', borderBottom: '1px solid var(--theme-border-light)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 style={{ fontSize: '24px', fontWeight: 700, color: 'var(--theme-text)', margin: 0 }}>Week {selectedWeek}</h1>
            <SnapshotIndicator dataSource={dataSource} snapshotDate={snapshotDate} week={selectedWeek} year={selectedYear} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <CaptureSnapshotButton week={selectedWeek} year={selectedYear} token={token || ''} userRole={user?.role || ''} dataSource={dataSource} onCapture={() => { fetchTopClients(selectedWarehouse, selectedWeek, selectedYear); calculateStats(selectedWarehouse); }} />
            {user.role === 'admin' && <button style={{ padding: '6px 12px', backgroundColor: 'var(--theme-background-secondary)', border: '1.5px solid var(--theme-border-light)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: 'var(--theme-text)', transition: 'all 0.2s' }} onClick={() => { setSelectedWarehouse(null); setSelectedArea(null); }}>↻ Reset</button>}
            <button style={{ padding: '6px 12px', backgroundColor: 'var(--theme-primary)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', transition: 'all 0.2s' }} onClick={handleRefresh}>🔄 Refresh</button>
          </div>
        </div>

        {/* UTILIZATION TAB */}
        {activeTab === 'utilization' && <>
          {/* STATS ROW - COMPACT */}
          <div style={styles.statsRow}>
            <div style={{ background: 'linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-accent) 100%)', borderRadius: '12px', padding: '20px 16px', textAlign: 'center', boxShadow: 'var(--theme-shadow-lg)', transition: 'all 0.3s ease' }}>
              <div style={{ fontSize: '36px', fontWeight: 700, color: 'white', marginBottom: '6px' }}>{overview?.UTILIZATION_PCT?.toFixed(1) || '0'}%</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Utilization</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, var(--theme-card) 0%, var(--theme-background-secondary) 100%)', borderRadius: '12px', padding: '20px 16px', textAlign: 'center', boxShadow: 'var(--theme-shadow-lg)', transition: 'all 0.3s ease', border: '1px solid var(--theme-border-light)' }}>
              <div style={{ fontSize: '36px', fontWeight: 700, color: 'var(--theme-text)', marginBottom: '6px' }}>{overview?.TOTAL_PALLETS?.toLocaleString() || '0'}</div>
              <div style={{ fontSize: '11px', color: 'var(--theme-text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Pallets</div>
            </div>
            <div style={{ background: 'linear-gradient(135deg, var(--theme-success) 0%, rgba(var(--theme-success-rgb), 0.7) 100%)', borderRadius: '12px', padding: '20px 16px', textAlign: 'center', boxShadow: 'var(--theme-shadow-lg)', transition: 'all 0.3s ease' }}>
              <div style={{ fontSize: '36px', fontWeight: 700, color: 'white', marginBottom: '6px' }}>{overview?.CAPACITY?.toLocaleString() || '0'}</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>Capacity</div>
            </div>
          </div>

          {/* CHARTS ROW */}
          <div style={styles.chartsGrid}>
            {/* GAUGE */}
            <div style={styles.chartCard}><h3 style={styles.chartTitle}>Utilization Gauge</h3><div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}><div style={{ position: 'relative', width: '200px', height: '130px' }}><div style={{ position: 'absolute', top: 0, left: '10px', width: '180px', height: '180px', borderRadius: '50%', background: `conic-gradient(#E0E0E0 0deg 180deg, transparent 180deg 360deg)`, transform: 'rotate(-90deg)' }} /><div style={{ position: 'absolute', top: 0, left: '10px', width: '180px', height: '180px', borderRadius: '50%', background: `conic-gradient(${utilColor} 0deg ${Math.min((overview?.UTILIZATION_PCT || 0), 100) * 1.8}deg, transparent ${Math.min((overview?.UTILIZATION_PCT || 0), 100) * 1.8}deg 360deg)`, transform: 'rotate(-90deg)' }} /><div style={{ position: 'absolute', top: '20px', left: '30px', width: '140px', height: '140px', borderRadius: '50%', backgroundColor: 'white' }} /><div style={{ position: 'absolute', top: '65px', left: '0', right: '0', textAlign: 'center' }}><div style={{ fontSize: '32px', fontWeight: 700, color: utilColor }}>{overview?.UTILIZATION_PCT?.toFixed(1) || '0'}%</div></div></div></div></div>

            {/* TOP CLIENTS */}
            <div style={styles.chartCard}><h3 style={styles.chartTitle}><span>Top Clients ({topClients.length})</span></h3><div style={styles.legendRow}><div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: colors.primary }}></div>IBM</div><div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: colors.secondary }}></div>Manual</div><div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: colors.success }}></div>Merged</div><div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: colors.textMuted }}></div>Previous</div></div><div style={{ maxHeight: '400px', overflowY: 'auto' }}>{topClients.length === 0 ? <p style={{ color: colors.textSecondary, textAlign: 'center', padding: '30px', fontSize: '12px' }}>No data</p> : <ResponsiveContainer width="100%" height={Math.max(80, topClients.length * 40)}><BarChart data={topClients} layout="vertical" margin={{ left: 60, right: 30, top: 10, bottom: 10 }} barGap={2} barCategoryGap={15}><CartesianGrid strokeDasharray="3 3" horizontal vertical={false} /><XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v.toString()} /><YAxis type="category" dataKey="CLIENT" tick={{ fontSize: 10 }} width={55} tickFormatter={(v) => v?.length > 10 ? v.substring(0, 10) + '..' : v} /><Tooltip content={({ active, payload }: any) => { if (!active || !payload?.length) return null; const d = payload[0]?.payload; return (<div style={{ background: 'white', border: '1px solid #E8E8E8', borderRadius: '6px', padding: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', fontSize: '12px' }}><div style={{ fontWeight: 600, marginBottom: '4px' }}>{d?.CLIENT}<span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '3px', marginLeft: '6px', backgroundColor: getBarColor(d?.SOURCE) + '20', color: getBarColor(d?.SOURCE) }}>{d?.SOURCE || 'IBM'}</span></div><div>Current: <strong>{d?.CURRENT_PALLETS?.toLocaleString()}</strong></div><div>Previous: <strong>{d?.PREVIOUS_PALLETS?.toLocaleString()}</strong></div>{d?.CURRENT_PALLETS !== d?.PREVIOUS_PALLETS && <div style={{ color: d?.CURRENT_PALLETS > d?.PREVIOUS_PALLETS ? colors.success : colors.danger }}>Change: {d?.CURRENT_PALLETS > d?.PREVIOUS_PALLETS ? '+' : ''}{(d?.CURRENT_PALLETS - d?.PREVIOUS_PALLETS)?.toLocaleString()}</div>}</div>); }} /><Bar dataKey="CURRENT_PALLETS" name="Current" barSize={10} radius={[0, 3, 3, 0]} cursor="pointer" onClick={(d: any) => { if (d?.CLIENT) { setSelectedClient(d.CLIENT); setSelectedClientSource(d.SOURCE || 'IBM'); setShowClientModal(true); } }}>{topClients.map((e, i) => <Cell key={`cell-${i}`} fill={getBarColor(e.SOURCE)} />)}</Bar><Bar dataKey="PREVIOUS_PALLETS" fill={colors.textMuted} name="Previous" barSize={10} radius={[0, 3, 3, 0]} /></BarChart></ResponsiveContainer>}</div></div>
          </div>

          {/* WAREHOUSE CHART */}

          
          <div style={styles.fullWidthCard}><h3 style={styles.chartTitle}><span>Pallet vs Capacity</span>{user.role === 'admin' && <select value={selectedWarehouse || ''} onChange={(e) => handleWarehouseSelect(e.target.value || null)} style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #E8E8E8', fontSize: '11px' }}><option value="">All</option>{warehouses.map(w => <option key={w.WAREHOUSE} value={w.WAREHOUSE}>{getWarehouseDisplayName(w.WAREHOUSE)}</option>)}</select>}</h3><div style={styles.legendRow}><div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: colors.primary }}></div>IBM</div><div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: colors.secondary }}></div>Manual</div><div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: colors.success }}></div>Merged</div><div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#D35400' }}></div>Temp</div><div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: colors.textMuted }}></div>Capacity</div><div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: colors.accent, borderRadius: '50%' }}></div>%</div></div><div style={{ height: '280px' }}><ResponsiveContainer width="100%" height="100%"><ComposedChart data={warehouses} margin={{ top: 10, right: 40, left: 10, bottom: 50 }} barCategoryGap="20%"><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="WAREHOUSE" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={50} tickFormatter={getWarehouseDisplayName} /><YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v.toString()} /><YAxis yAxisId="right" orientation="right" domain={[0, 120]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} /><Tooltip content={({ active, payload, label }: any) => { if (!active || !payload?.length) return null; const data = payload[0]?.payload; const srcColor = getBarColor(data?.SOURCE); return (<div style={{ background: 'white', border: '1px solid #E8E8E8', borderRadius: '6px', padding: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', fontSize: '12px' }}><div style={{ fontWeight: 600, marginBottom: '6px', borderBottom: '1px solid #E8E8E8', paddingBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>{getWarehouseDisplayName(label)}<span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '3px', backgroundColor: srcColor + '20', color: srcColor }}>{data?.SOURCE || 'IBM'}</span></div><div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '3px' }}><span style={{ color: colors.textMuted }}>Capacity:</span><span style={{ fontWeight: 600 }}>{data?.CAPACITY?.toLocaleString()}</span></div><div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '3px' }}><span style={{ color: srcColor }}>Pallets:</span><span style={{ fontWeight: 600 }}>{data?.NORMAL_PALLETS?.toLocaleString()}</span></div>{(data?.TEMP_BLOCKED || 0) > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '3px' }}><span style={{ color: '#D35400' }}>Temp Blocked:</span><span style={{ fontWeight: 600, color: '#D35400' }}>{data?.TEMP_BLOCKED?.toLocaleString()}</span></div>}<div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '3px', borderTop: '1px solid #E8E8E8', paddingTop: '4px' }}><span style={{ color: colors.text }}>Total:</span><span style={{ fontWeight: 700 }}>{data?.PALLET_COUNT?.toLocaleString()}</span></div><div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}><span style={{ color: colors.accent }}>Utilization:</span><span style={{ fontWeight: 600, color: colors.accent }}>{data?.UTILIZATION_PCT?.toFixed(1)}%</span></div></div>); }} /><Bar yAxisId="left" dataKey="CAPACITY" fill={colors.textMuted} name="Capacity" radius={[3, 3, 0, 0]} /><Bar yAxisId="left" dataKey="NORMAL_PALLETS" stackId="pallets" name="Pallets" radius={[0, 0, 0, 0]} cursor="pointer" onClick={(d: any) => handleWarehouseSelect(d?.WAREHOUSE)}>{warehouses.map(e => <Cell key={e.WAREHOUSE} fill={getBarColor(e.SOURCE)} />)}</Bar><Bar yAxisId="left" dataKey="TEMP_BLOCKED" stackId="pallets" fill="#D35400" name="Temp Blocked" radius={[3, 3, 0, 0]} cursor="pointer" onClick={(d: any) => handleWarehouseSelect(d?.WAREHOUSE)} /><Line yAxisId="right" type="monotone" dataKey="UTILIZATION_PCT" stroke={colors.accent} strokeWidth={2} dot={(props: any) => <circle key={`dot-${props.index}`} cx={props.cx - 12} cy={props.cy} r={4} fill={colors.accent} stroke="#fff" strokeWidth={2} />} activeDot={{ r: 6 }} name="%" /></ComposedChart></ResponsiveContainer></div></div>

          {/* BIN LINK */}
          <div style={{ backgroundColor: '#EBF5FB', borderRadius: '10px', padding: '14px 18px', border: '1px solid #AED6F1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><div><div style={{ fontWeight: 600, color: colors.text, fontSize: '14px' }}>📦 Bin Occupancy</div><div style={{ fontSize: '11px', color: colors.textSecondary }}>View detailed bin status</div></div><button onClick={() => setActiveTab('bins')} style={{ padding: '8px 14px', backgroundColor: colors.primary, color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>View →</button></div>
        </>}

        {/* BINS TAB */}
        {activeTab === 'bins' && <BinOccupancySection token={token} selectedWarehouse={selectedWarehouse} warehouses={warehouses} />}

        {/* WEEKLY TAB */}
        {activeTab === 'weekly' && <>{loading ? <div style={{ ...styles.fullWidthCard, textAlign: 'center', padding: '40px' }}><div style={{ fontSize: '32px', marginBottom: '10px' }}>📅</div><div style={{ fontSize: '13px', color: colors.textSecondary }}>Loading...</div></div> : <>
          {weeklyTrendData.length > 0 && <div style={styles.fullWidthCard}><h3 style={styles.chartTitle}><span>In/Out Trend - {selectedYear}</span><select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #E8E8E8', fontSize: '11px' }}>{[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}</select></h3><div style={{ height: '200px' }}><ResponsiveContainer width="100%" height="100%"><AreaChart data={weeklyTrendData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="week" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Area type="monotone" dataKey="inbound" stackId="1" stroke={colors.success} fill="#C6EFCE" name="In" /><Area type="monotone" dataKey="outbound" stackId="2" stroke={colors.danger} fill="#FFC7CE" name="Out" /><Line type="monotone" dataKey="closing" stroke={colors.primary} strokeWidth={2} name="Close" /></AreaChart></ResponsiveContainer></div></div>}
          <div style={styles.fullWidthCard}><h3 style={styles.chartTitle}><span>Entries</span><div style={{ display: 'flex', gap: '8px' }}><button style={{ ...styles.addBtn, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }} onClick={() => setShowQuickMovement(true)}>⚡ Quick</button><button style={styles.addBtn} onClick={() => { setEditEntry(null); setShowWeeklyEntry(true); }}>+ Full Form</button></div></h3>{weeklyEntries.length > 0 ? <div style={{ overflowX: 'auto' }}><table style={styles.table}><thead><tr><th style={styles.th}>Wk</th><th style={styles.th}>WH</th><th style={styles.th}>Client</th><th style={styles.th}>Open</th><th style={{ ...styles.th, color: colors.success }}>In</th><th style={{ ...styles.th, color: colors.danger }}>Out</th><th style={styles.th}>Close</th><th style={styles.th}>%</th><th style={styles.th}>Act</th></tr></thead><tbody>{weeklyEntries.filter(e => !selectedWarehouse || e.warehouse === selectedWarehouse).slice(0, 20).map(e => { const u = e.capacity > 0 ? Math.round((e.closing_balance / e.capacity) * 100) : 0; return <tr key={e.id}><td style={styles.td}>W{e.week_number}</td><td style={styles.td}>{getWarehouseDisplayName(e.warehouse)}</td><td style={styles.td}><strong>{e.client_name}</strong></td><td style={styles.td}>{e.opening_balance?.toLocaleString()}</td><td style={{ ...styles.td, color: colors.success }}>+{e.inbound?.toLocaleString()}</td><td style={{ ...styles.td, color: colors.danger }}>-{e.outbound?.toLocaleString()}</td><td style={{ ...styles.td, fontWeight: 'bold' }}>{e.closing_balance?.toLocaleString()}</td><td style={styles.td}><span style={{ padding: '2px 6px', borderRadius: '3px', backgroundColor: u > 90 ? '#FFC7CE' : u > 70 ? '#FFEB9C' : '#C6EFCE', color: u > 90 ? colors.danger : u > 70 ? '#9C6500' : colors.success, fontWeight: 600, fontSize: '10px' }}>{u}%</span></td><td style={styles.td}><button style={{ ...styles.actionBtn, backgroundColor: '#E8F4FD', color: colors.primary }} onClick={() => { setEditEntry(e); setShowWeeklyEntry(true); }}>✎</button><button style={{ ...styles.actionBtn, backgroundColor: '#FFC7CE', color: colors.danger }} onClick={() => handleDeleteEntry(e.id)}>✕</button></td></tr>; })}</tbody></table></div> : <p style={{ color: colors.textSecondary, textAlign: 'center', padding: '30px', fontSize: '12px' }}>No entries</p>}</div>
        </>}</>}

        {/* TRENDS TAB */}
        {activeTab === 'trends' && <TrendsTab weeklyEntries={weeklyEntries} selectedWarehouse={selectedWarehouse} selectedYear={selectedYear} token={token} />}

        {/* 3D VIEW TAB */}
        {activeTab === '3d' && (
          <div style={{ ...styles.fullWidthCard, height: 'calc(100vh - 200px)', padding: 0, overflow: 'hidden' }}>
            <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: colors.textSecondary }}>Loading 3D View...</div>}>
              <Warehouse3D
                warehouseCode={selectedWarehouse || 'JDB'}
                data={rawWarehouseData.filter(d => d.WAREHOUSE === (selectedWarehouse || 'JDB'))}
                onBinClick={(bin) => console.log('Bin clicked:', bin)}
              />
            </Suspense>
          </div>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && user.role === 'admin' && <UserManagementPanel token={token} warehouses={warehouses.map(w => w.WAREHOUSE)} />}
      </div>

      {/* MODALS */}
      <WeeklyEntryModal isOpen={showWeeklyEntry} onClose={() => { setShowWeeklyEntry(false); setEditEntry(null); }} onSave={fetchManualEntries} user={user} token={token} editEntry={editEntry} warehouses={warehouses.map(w => w.WAREHOUSE)} clients={clientsList} allEntries={weeklyEntries} />
      <QuickMovementForm isOpen={showQuickMovement} onClose={() => setShowQuickMovement(false)} onSave={fetchManualEntries} token={token} warehouses={warehouses.map(w => w.WAREHOUSE)} clients={clientsList} allEntries={weeklyEntries} selectedWeek={selectedWeek} selectedYear={selectedYear} />
      <ClientDrillDownModal isOpen={showClientModal} onClose={() => { setShowClientModal(false); setSelectedClient(null); }} client={selectedClient} warehouse={selectedWarehouse} source={selectedClientSource} manualEntries={weeklyEntries.filter(e => e.week_number === selectedWeek && e.year === selectedYear)} />
      {showTeamsSettings && <TeamsSettings onClose={() => setShowTeamsSettings(false)} token={token || ''} />}
    </div>
  );
};

export default UtilizationDashboard;
